import React, { useState, useEffect } from 'react';
import { getStatistics, getUpcomingReminders, getRecentActivities, getAllDeals, getEmployees, getDealAssignments, getPriorityDashboard, getDailyReport, getWeeklyReport, completeReminder } from '../utils/api';
import { ar, en } from '../utils/translations';
import SellMarketOps from './SellMarketOps';
// React Icons - Colorful icons
import { 
  FaUsers, 
  FaSearch, 
  FaClipboardList, 
  FaClock,
  FaFire,
  FaCircle,
  FaPhone,
  FaWhatsapp,
  FaEnvelope,
  FaHandshake,
  FaEdit,
  FaFileAlt,
  FaMoneyBillWave,
  FaChartLine,
  FaUserFriends,
  FaArrowRight,
  FaSync,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaArrowUp,
  FaMinus,
  FaArrowDown,
  FaBell,
  FaCheckCircle,
  FaCalculator,
  FaChevronDown,
  FaRobot,
  FaSpinner,
  FaHome,
  FaHourglassHalf,
  FaBullhorn,
  FaBuilding,
  FaStopwatch
} from 'react-icons/fa';

// Orange + Yellow Cream Palette (Lighter, Yellow-Based, Less Orange)
const colors = {
  primary: '#f97316',           // Primary orange (brand color)
  primaryDark: '#ea580c',      // Darker orange for hover states
  primaryLight: '#fdba74',      // Lighter orange for accents
  secondary: '#fff4e0',        // Yellow cream (secondary color)
  secondaryLight: '#fff8ed',    // Lighter yellow cream
  background: '#fffef8',        // Very light yellow cream background
  backgroundLight: '#fffefb',  // Almost white with slight yellow tint
  border: '#ffecc8',            // Light yellow cream border
  borderHover: '#fff2d8',       // Hover border (lighter yellow cream)
  text: '#1a1a1a',              // Black text
  textSecondary: '#4a4a4a',     // Dark gray text (secondary)
  textMuted: '#6a6a6a',         // Medium gray text
  // Priority colors - all in orange/yellow cream palette
  critical: { bg: '#fff4e6', border: '#f97316', text: '#9a3412', icon: '#ea580c' },
  high: { bg: '#fff7ed', border: '#f97316', text: '#9a3412', icon: '#f97316' },
  medium: { bg: '#fff8ed', border: '#ffecc8', text: '#4a4a4a', icon: '#ffecc8' },
  low: { bg: '#fffaf5', border: '#fff2d8', text: '#6a6a6a', icon: '#fff2d8' },
  // Status colors - using orange/yellow cream only
  success: '#f97316',           // Orange for completed
  info: '#f97316',              // Orange for info
  warning: '#ea580c',           // Darker orange for warnings
  // Neutral backgrounds
  neutral: '#fffef8',           // Very light yellow cream
  neutralLight: '#fffefb',      // Almost white with yellow tint
  kpiBackground: '#fffaf5'     // Light yellow cream for KPI boxes
};

const Dashboard = ({ language = 'ar', onNavigate, refreshTrigger, userRole }) => {
  const t = language === 'ar' ? ar : en;
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(null);
  const [deals, setDeals] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dealAssignments, setDealAssignments] = useState({});
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [priorityData, setPriorityData] = useState({
    summary: {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      overdue: 0,
      total: 0
    },
    priorities: {
      critical: [],
      high: [],
      medium: [],
      low: [],
      overdue: []
    },
    today: []
  });
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showReport, setShowReport] = useState(null); // 'daily' or 'weekly' or null
  
  // Marketing KPIs state
  const [marketingKpis, setMarketingKpis] = useState(null);
  
  // Employee's marketing tasks (for non-owners)
  const [myTasks, setMyTasks] = useState([]);
  const [loadingMyTasks, setLoadingMyTasks] = useState(false);
  const [showListingForm, setShowListingForm] = useState(null); // task object
  const [listingFormData, setListingFormData] = useState({
    title: '',
    description: '',
    platform: 'حراج',
    price: '',
    external_url: '',
    fal_license: '',
    ad_license: '',
    platform_ad_number: '',
    has_brokerage_contract: false
  });
  const [submittingListing, setSubmittingListing] = useState(false);
  const [pendingMarketingItems, setPendingMarketingItems] = useState([]);
  const [loadingMarketing, setLoadingMarketing] = useState(false);
  
  // Per-Employee KPIs state
  const [employeeKpis, setEmployeeKpis] = useState(null);
  const [loadingEmployeeKpis, setLoadingEmployeeKpis] = useState(false);
  
  // Response SLA state
  const [responseSla, setResponseSla] = useState(null);
  const [loadingResponseSla, setLoadingResponseSla] = useState(false);
  
  // REGA Violations state
  const [regaViolations, setRegaViolations] = useState(null);
  const [loadingRegaViolations, setLoadingRegaViolations] = useState(false);
  
  const isOwner = userRole === 'owner';
  const isManager = userRole === 'manager';
  const isOwnerOrManager = isOwner || isManager;
  
  // Debug: Log priorityData changes
  useEffect(() => {
    if (priorityData) {
      console.log('🔄 Priority data state changed:', {
        summary: priorityData.summary,
        prioritiesCounts: {
          critical: priorityData.priorities?.critical?.length || 0,
          high: priorityData.priorities?.high?.length || 0,
          medium: priorityData.priorities?.medium?.length || 0,
          low: priorityData.priorities?.low?.length || 0,
          overdue: priorityData.priorities?.overdue?.length || 0
        },
        todayCount: priorityData.today?.length || 0
      });
      if (priorityData.priorities?.medium?.length > 0) {
        console.log('🟡 Medium priorities in state:', priorityData.priorities.medium);
      }
    }
  }, [priorityData]);

  useEffect(() => {
    loadData();
    if (isOwnerOrManager) {
      loadDealsData();
      loadPriorityData();
      loadMarketingData();
      loadEmployeeKpis();
      loadResponseSla();
      loadRegaViolations();
    } else {
      // Employee: load their marketing tasks
      loadMyTasks();
    }
  }, [refreshTrigger, isOwnerOrManager]);

  // Refresh when component becomes visible (user navigates back to dashboard)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
        if (isOwnerOrManager) {
          loadDealsData();
          loadPriorityData(); // Refresh priority data when visible
          loadMarketingData(); // Refresh marketing data when visible
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also refresh when window gains focus
    const handleFocus = () => {
      loadData();
      if (isOwnerOrManager) {
        loadDealsData();
        loadPriorityData(); // Refresh priority data on focus
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isOwnerOrManager]);

  // Clear reports when language changes to avoid mixed content
  useEffect(() => {
    // Clear any displayed reports when language changes
    // User will need to click the button again to reload with new language
    if (showReport === 'weekly') {
      setWeeklyReport(null);
      setShowReport(null);
    } else if (showReport === 'daily') {
      setDailyReport(null);
      setShowReport(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const loadDealsData = async () => {
    try {
      setLoadingDeals(true);
      const [dealsResponse, employeesResponse] = await Promise.all([
        getAllDeals(),
        getEmployees()
      ]);
      
      const dealsData = dealsResponse.data || [];
      setDeals(dealsData);
      setEmployees(employeesResponse.data || []);
      
      // Load assignments for all deals
      const assignments = {};
      for (const deal of dealsData) {
        try {
          const assignResponse = await getDealAssignments(deal.id);
          console.log(`Dashboard - Assignments for deal ${deal.id}:`, assignResponse.data);
          assignments[deal.id] = assignResponse.data || [];
        } catch (error) {
          console.error(`Dashboard - Error loading assignments for deal ${deal.id}:`, error);
          assignments[deal.id] = [];
        }
      }
      console.log('Dashboard - All assignments loaded:', assignments);
      setDealAssignments(assignments);
    } catch (error) {
      console.error('Error loading deals data:', error);
    } finally {
      setLoadingDeals(false);
    }
  };

  const loadPriorityData = async () => {
    try {
      setLoadingPriorities(true);
      console.log('🔄 Loading priority data...');
      const response = await getPriorityDashboard();
      console.log('✅ Priority data loaded:', response.data);
      console.log('📊 Summary:', response.data?.summary);
      console.log('📋 Priorities object:', response.data?.priorities);
      if (response.data?.priorities) {
        console.log('  - Critical:', response.data.priorities.critical?.length || 0);
        console.log('  - High:', response.data.priorities.high?.length || 0);
        console.log('  - Medium:', response.data.priorities.medium?.length || 0);
        console.log('  - Low:', response.data.priorities.low?.length || 0);
      }
      console.log('📋 Today items:', response.data?.today?.length);
      
      // Ensure priorities object and summary exist
      if (response.data) {
        if (!response.data.priorities) {
          console.warn('⚠️ No priorities object in response, creating empty one');
          response.data.priorities = {
            critical: [],
            high: [],
            medium: [],
            low: [],
            overdue: []
          };
        }
        if (!response.data.summary) {
          console.warn('⚠️ No summary object in response, creating empty one');
          response.data.summary = {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            overdue: 0,
            total: 0
          };
        }
        if (!response.data.today) {
          response.data.today = [];
        }
        
        console.log('📦 Setting priority data with:', {
          summary: response.data.summary,
          prioritiesCounts: {
            critical: response.data.priorities?.critical?.length || 0,
            high: response.data.priorities?.high?.length || 0,
            medium: response.data.priorities?.medium?.length || 0,
            low: response.data.priorities?.low?.length || 0,
            overdue: response.data.priorities?.overdue?.length || 0
          },
          todayCount: response.data.today?.length || 0
        });
        
        // Log medium priorities if any
        if (response.data.priorities?.medium?.length > 0) {
          console.log('🟡 Medium priorities being set:', response.data.priorities.medium.map(d => ({
            id: d.id,
            title: d.title,
            score: d.priority_score,
            level: d.priority_level,
            type: d.item_type
          })));
        }
        
        setPriorityData(response.data);
        
        // Verify state was set (check on next render)
        setTimeout(() => {
          console.log('🔍 Priority data state after setting (checking in next render)...');
        }, 100);
      } else {
        // If response.data is null/undefined, set empty structure
        console.warn('⚠️ No data in response, creating empty structure');
        setPriorityData({
          summary: {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            overdue: 0,
            total: 0
          },
          priorities: {
            critical: [],
            high: [],
            medium: [],
            low: [],
            overdue: []
          },
          today: []
        });
      }
    } catch (error) {
      console.error('❌ Error loading priority data:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Set empty structure instead of null so the section still displays
      setPriorityData({
        summary: {
          critical: 0,
          high: 0,
          medium: 0,
          low: 0,
          overdue: 0,
          total: 0
        },
        priorities: {
          critical: [],
          high: [],
          medium: [],
          low: [],
          overdue: []
        },
        today: []
      });
    } finally {
      setLoadingPriorities(false);
    }
  };

  // Load marketing KPIs and pending items
  const loadMarketingData = async () => {
    console.log('📢 Loading marketing data...');
    try {
      setLoadingMarketing(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const [kpisRes, pendingRes] = await Promise.all([
        fetch(`${API_BASE}/api/marketing/kpis?days=30`, { headers }),
        fetch(`${API_BASE}/api/marketing/pending`, { headers })
      ]);
      
      console.log('📢 Marketing KPIs response status:', kpisRes.status);
      console.log('📢 Marketing pending response status:', pendingRes.status);
      
      if (kpisRes.ok) {
        const kpisData = await kpisRes.json();
        console.log('📢 Marketing KPIs data:', kpisData);
        setMarketingKpis(kpisData);
      } else {
        console.error('📢 Marketing KPIs error:', await kpisRes.text());
        // Set default data structure even on error
        setMarketingKpis({
          units: { total: 0, vacant: 0, reserved: 0, rented: 0 },
          listings: { total: 0, pending: 0, active: 0, closed: 0, sla_met: 0, sla_breached: 0, sla_rate: null },
          team: []
        });
      }
      
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        console.log('📢 Pending marketing items:', pendingData);
        setPendingMarketingItems(pendingData);
      } else {
        console.error('📢 Marketing pending error:', await pendingRes.text());
        setPendingMarketingItems([]);
      }
    } catch (error) {
      console.error('📢 Error loading marketing data:', error);
      // Set default data on error so section still shows
      setMarketingKpis({
        units: { total: 0, vacant: 0, reserved: 0, rented: 0 },
        listings: { total: 0, pending: 0, active: 0, closed: 0, sla_met: 0, sla_breached: 0, sla_rate: null },
        team: []
      });
      setPendingMarketingItems([]);
    } finally {
      setLoadingMarketing(false);
    }
  };

  // Load Per-Employee KPIs (for owner view)
  const loadEmployeeKpis = async () => {
    try {
      setLoadingEmployeeKpis(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      // Use same date range as marketing KPIs (30 days)
      const res = await fetch(`${API_BASE}/api/marketing/employee-kpis?days=30`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setEmployeeKpis(data);
      }
    } catch (error) {
      console.error('Error loading employee KPIs:', error);
    } finally {
      setLoadingEmployeeKpis(false);
    }
  };

  // Load Response SLA Dashboard (for owner view)
  const loadResponseSla = async () => {
    try {
      setLoadingResponseSla(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/response-sla/dashboard?days=30`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setResponseSla(data);
      }
    } catch (error) {
      console.error('Error loading response SLA:', error);
    } finally {
      setLoadingResponseSla(false);
    }
  };

  // Load REGA Violations (for owner view)
  const loadRegaViolations = async () => {
    try {
      setLoadingRegaViolations(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/rega/violations`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setRegaViolations(data);
      }
    } catch (error) {
      console.error('Error loading REGA violations:', error);
    } finally {
      setLoadingRegaViolations(false);
    }
  };

  // Load employee's marketing tasks (for non-owners)
  const loadMyTasks = async () => {
    try {
      setLoadingMyTasks(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/marketing/tasks`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setMyTasks(data);
      } else {
        setMyTasks([]);
      }
    } catch (error) {
      console.error('Error loading my tasks:', error);
      setMyTasks([]);
    } finally {
      setLoadingMyTasks(false);
    }
  };

  // Open listing form for a task
  const openListingForm = (task) => {
    const unitTypeAr = task.unit_type === 'apartment' ? 'شقة' : 
                       task.unit_type === 'villa' ? 'فيلا' : 
                       task.unit_type === 'office' ? 'مكتب' : 
                       task.unit_type === 'shop' ? 'محل' : 
                       task.unit_type === 'warehouse' ? 'مستودع' : 
                       task.unit_type || 'شقة';
    const savedFalLicense = localStorage.getItem('fal_license') || '';
    setListingFormData({
      title: `${unitTypeAr} للإيجار - ${task.unit_number}`,
      description: '',
      platform: 'حراج',
      price: task.monthly_rent || '',
      external_url: '',
      fal_license: savedFalLicense,
      ad_license: '',
      platform_ad_number: '',
      has_brokerage_contract: false
    });
    setShowListingForm(task);
  };

  // Submit listing for a task
  const handleCreateListing = async (e) => {
    e.preventDefault();
    if (!showListingForm) return;
    
    setSubmittingListing(true);
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001');
      
      const res = await fetch(`${API_BASE}/api/marketing/tasks/${showListingForm.id}/create-listing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(listingFormData)
      });
      
      if (res.ok) {
        const result = await res.json();
        setShowListingForm(null);
        loadMyTasks(); // Refresh
        alert(language === 'ar' 
          ? `تم إنشاء الإعلان بنجاح! ${result.sla_met ? '✅ ضمن المهلة' : '⚠️ تجاوز المهلة'}`
          : `Listing created! ${result.sla_met ? '✅ Within SLA' : '⚠️ SLA exceeded'}`);
      } else {
        throw new Error('Failed to create listing');
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      alert(language === 'ar' ? 'فشل في إنشاء الإعلان' : 'Failed to create listing');
    } finally {
      setSubmittingListing(false);
    }
  };

  // Platform options
  const platforms = [
    { value: 'حراج', label: 'حراج' },
    { value: 'عقار', label: 'عقار' },
    { value: 'سكني', label: 'سكني' },
    { value: 'بيوت', label: 'بيوت' },
    { value: 'other', label: language === 'ar' ? 'أخرى' : 'Other' }
  ];

  // Also refresh when component mounts or becomes visible
  useEffect(() => {
    const interval = setInterval(() => {
      // Refresh every 30 seconds if dashboard is visible
      if (document.visibilityState === 'visible') {
        loadData();
        if (isOwnerOrManager) {
          loadDealsData();
          loadPriorityData(); // Refresh priority data too
          loadMarketingData(); // Refresh marketing data too
        } else {
          loadMyTasks(); // Refresh employee tasks
        }
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isOwnerOrManager]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Force fresh data by adding timestamp to prevent caching
      const [statsRes, remindersRes] = await Promise.all([
        getStatistics(),
        getUpcomingReminders()
      ]);
      
      // Debug: Log the full response
      console.log('========================================');
      console.log('DASHBOARD - Full API Response:');
      console.log('statsRes:', statsRes);
      console.log('statsRes.data:', statsRes.data);
      console.log('statsRes.data.totalClients:', statsRes.data?.totalClients);
      console.log('Type of totalClients:', typeof statsRes.data?.totalClients);
      console.log('========================================');
      
      setStats(statsRes.data);
      setReminders(remindersRes.data || []);
      
      // Also log after setting state
      console.log('Dashboard state after update:', statsRes.data);
      console.log('Leads by Importance:', statsRes.data?.leadsByImportance);
      console.log('Leads by Status:', statsRes.data?.leadsByStatus);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    try {
      setLoadingActivities(true);
      const response = await getRecentActivities(30); // Get last 30 activities
      setRecentActivities(response.data || []);
    } catch (error) {
      console.error('Error loading recent activities:', error);
      setRecentActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleShowActivities = () => {
    setShowDetails('activities');
    if (recentActivities.length === 0) {
      loadRecentActivities();
    }
  };

  const getActivityIcon = (type) => {
    const iconStyle = { fontSize: '16px', color: colors.primary };
    switch(type) {
      case 'call': return <FaPhone style={iconStyle} />;
      case 'whatsapp': return <FaWhatsapp style={iconStyle} />;
      case 'email': return <FaEnvelope style={iconStyle} />;
      case 'meeting': return <FaHandshake style={iconStyle} />;
      case 'note': return <FaEdit style={iconStyle} />;
      default: return <FaFileAlt style={iconStyle} />;
    }
  };

  if (loading) {
    return (
      <div className="card">
        <p>{t.loading}</p>
      </div>
    );
  }

  const loadDailyReport = async () => {
    try {
      setLoadingReports(true);
      const response = await getDailyReport();
      setDailyReport(response.data);
      setShowReport('daily');
    } catch (error) {
      console.error('Error loading daily report:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const loadWeeklyReport = async (lang = language) => {
    try {
      setLoadingReports(true);
      // Clear previous report to avoid mixed language issues
      if (showReport === 'weekly') {
        setWeeklyReport(null);
      }
      const response = await getWeeklyReport(lang);
      console.log('📊 Weekly Report Response:', response.data);
      setWeeklyReport(response.data);
      setShowReport('weekly');
    } catch (error) {
      console.error('❌ Error loading weekly report:', error);
      console.error('   Error details:', error.response?.data || error.message);
      alert(lang === 'ar' 
        ? 'حدث خطأ في تحميل التقرير الأسبوعي. تحقق من وحدة التحكم للتفاصيل.'
        : 'Error loading weekly report. Check console for details.'
      );
    } finally {
      setLoadingReports(false);
    }
  };

  // Helper function to translate reminder title from Arabic to English
  const translateReminderTitle = (title, lang) => {
    if (lang === 'ar' || !title) return title;
    
    // Pattern: "متابعة مع {name} (عميل محتمل {importance})" or "متابعة مع {name} (عميل {importance})"
    const arabicPattern = /متابعة مع\s+(.+?)\s+\(عميل\s*(محتمل\s*)?(.+?)\)/;
    const match = title.match(arabicPattern);
    
    if (match) {
      const clientName = match[1].trim();
      const isProspect = match[2] ? true : false;
      const importanceAr = match[3].trim();
      
      // Translate importance
      const importanceMap = { 'مهم': 'important', 'متوسط': 'medium', 'عادي': 'low' };
      const importanceText = importanceMap[importanceAr] || 'medium';
      
      // Format in English
      if (isProspect) {
        return `Follow-up with ${clientName} (prospect ${importanceText})`;
      } else {
        return `Follow-up with ${clientName} (customer ${importanceText})`;
      }
    }
    
    // If pattern doesn't match, return original
    return title;
  };

  const handleRefresh = () => {
    loadData();
    if (isOwner) {
      loadDealsData();
      loadPriorityData(); // Also refresh priorities when main refresh is clicked
    }
  };

  // Helper function to determine reminder urgency
  const getReminderUrgency = (reminderDate) => {
    const now = new Date();
    const reminder = new Date(reminderDate);
    const diffHours = (reminder - now) / (1000 * 60 * 60);
    
    if (diffHours < 0) {
      const hoursAgo = Math.abs(Math.floor(diffHours));
      return { 
        level: 'overdue', 
        color: '#dc2626', 
        bg: '#fef2f2', 
        border: '#dc2626',
        label: language === 'ar' ? `متأخر ${hoursAgo}س` : `Overdue ${hoursAgo}h`,
        icon: '🔴'
      };
    }
    if (diffHours < 24) {
      const hoursLeft = Math.floor(diffHours);
      return { 
        level: 'today', 
        color: '#f97316', 
        bg: '#fff7ed', 
        border: '#fb923c',
        label: language === 'ar' ? `اليوم ${hoursLeft}س` : `Today ${hoursLeft}h`,
        icon: '🟠'
      };
    }
    if (diffHours < 168) { // 7 days
      const daysLeft = Math.floor(diffHours / 24);
      return { 
        level: 'week', 
        color: '#f59e0b', 
        bg: '#fffbeb', 
        border: '#fcd34d',
        label: language === 'ar' ? `${daysLeft} أيام` : `${daysLeft} days`,
        icon: '🟡'
      };
    }
    return { 
      level: 'later', 
      color: '#78716c', 
      bg: '#fafaf9', 
      border: '#e7e5e4',
      label: language === 'ar' ? 'لاحقاً' : 'Later',
      icon: '⚪'
    };
  };

  // Group reminders by urgency
  const groupRemindersByUrgency = (remindersList) => {
    const grouped = {
      overdue: [],
      today: [],
      week: [],
      later: []
    };
    
    remindersList.forEach(reminder => {
      const urgency = getReminderUrgency(reminder.reminder_date);
      grouped[urgency.level].push({ ...reminder, urgency });
    });
    
    return grouped;
  };

  // Handle completing a reminder
  const handleCompleteReminder = async (reminderId) => {
    try {
      await completeReminder(reminderId);
      // Refresh reminders
      const remindersRes = await getUpcomingReminders();
      setReminders(remindersRes.data || []);
      // Also refresh stats to update the count
      const statsRes = await getStatistics();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error completing reminder:', error);
      alert(language === 'ar' ? 'فشل في إكمال التذكير' : 'Failed to complete reminder');
    }
  };

  return (
    <div>
      {/* REGA Violations Alert (Critical - Top of Dashboard) */}
      {isOwnerOrManager && regaViolations && regaViolations.total > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
          border: '2px solid #dc2626',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          animation: 'pulse 2s infinite'
        }}>
          <style>
            {`@keyframes pulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
              50% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
            }`}
          </style>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '15px',
            marginBottom: '15px'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <FaExclamationTriangle style={{ color: 'white', fontSize: '24px' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#dc2626', fontSize: '1.2rem' }}>
                🚨 {language === 'ar' ? 'تنبيه REGA - إعلانات متجاوزة!' : 'REGA Alert - Overdue Ads!'}
              </h3>
              <p style={{ margin: '5px 0 0 0', color: '#7f1d1d', fontSize: '0.9rem' }}>
                {language === 'ar' 
                  ? `${regaViolations.total} إعلان يجب إزالته خلال يومين من استلام العربون (متطلب هيئة العقار)`
                  : `${regaViolations.total} ad(s) must be removed within 2 days of deposit (REGA requirement)`}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {regaViolations.violations.slice(0, 5).map((v, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 15px',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #fca5a5'
              }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#1f2937' }}>
                    {language === 'ar' ? 'الوحدة' : 'Unit'} {v.unit_number}
                  </span>
                  {v.client_name && (
                    <span style={{ color: '#6b7280', marginRight: '10px', marginLeft: '10px' }}>
                      | {v.client_name}
                    </span>
                  )}
                  {v.assigned_to_name && (
                    <span style={{ color: '#9333ea', marginRight: '10px', marginLeft: '10px', fontSize: '0.85rem' }}>
                      👤 {v.assigned_to_name}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{
                    padding: '4px 12px',
                    background: '#dc2626',
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {language === 'ar' ? `متأخر ${v.hours_overdue} ساعة` : `${v.hours_overdue}h overdue`}
                  </span>
                </div>
              </div>
            ))}
            {regaViolations.total > 5 && (
              <div style={{ textAlign: 'center', color: '#dc2626', fontSize: '0.9rem' }}>
                {language === 'ar' 
                  ? `و ${regaViolations.total - 5} إعلان آخر...`
                  : `and ${regaViolations.total - 5} more...`}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>{t.dashboard}</h2>
          <button
            onClick={handleRefresh}
            style={{
              padding: '8px 16px',
              background: colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '0.9rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.primaryDark;
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.primary;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FaSync />
            {language === 'ar' ? 'تحديث' : 'Refresh'}
          </button>
        </div>

        {/* Employee's Marketing Tasks - Show for non-owners */}
        {!isOwner && (
          <div style={{
            marginBottom: '25px',
            padding: '20px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
            borderRadius: '16px',
            border: '2px solid #fcd34d'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '15px'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#92400e', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                fontSize: '1.1rem'
              }}>
                <FaClipboardList style={{ color: '#d97706' }} />
                {language === 'ar' ? 'مهام التسويق' : 'Marketing Tasks'}
                {loadingMyTasks && (
                  <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>
                    ({language === 'ar' ? 'جاري التحميل...' : 'loading...'})
                  </span>
                )}
              </h3>
              <span style={{
                padding: '4px 12px',
                background: '#d97706',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600'
              }}>
                {myTasks.length} {language === 'ar' ? 'مهمة' : 'tasks'}
              </span>
            </div>

            {myTasks.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '30px', 
                color: '#6b7280',
                background: 'white',
                borderRadius: '12px'
              }}>
                <FaCheckCircle style={{ fontSize: '32px', color: '#16a34a', marginBottom: '10px' }} />
                <div>{language === 'ar' ? 'لا توجد مهام حالياً - أحسنت!' : 'No tasks currently - Great job!'}</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {myTasks.map(task => {
                  const isOverdue = task.is_overdue;
                  const hoursLeft = task.hours_until_deadline || 0;
                  const isApproaching = !isOverdue && hoursLeft <= 4;
                  
                  return (
                    <div
                      key={task.id}
                      style={{
                        padding: '15px',
                        background: 'white',
                        borderRadius: '12px',
                        border: `2px solid ${isOverdue ? '#dc2626' : isApproaching ? '#f59e0b' : '#e5e7eb'}`,
                        boxShadow: isOverdue ? '0 0 10px rgba(220, 38, 38, 0.2)' : 'none'
                      }}
                    >
                      {/* Task Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px', fontSize: '1rem' }}>
                            {language === 'ar' ? 'إنشاء إعلان للوحدة' : 'Create listing for unit'} {task.unit_number}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            {task.property_name || (language === 'ar' ? 'بدون عقار' : 'No property')}
                          </div>
                        </div>
                        <span style={{
                          padding: '4px 10px',
                          background: isOverdue ? '#fef2f2' : '#fef3c7',
                          color: isOverdue ? '#dc2626' : '#d97706',
                          borderRadius: '20px',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}>
                          {isOverdue 
                            ? (language === 'ar' ? 'متأخر!' : 'Overdue!')
                            : (language === 'ar' ? 'قيد الانتظار' : 'Pending')}
                        </span>
                      </div>

                      {/* Unit Info */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '10px',
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        fontSize: '0.85rem'
                      }}>
                        <div>
                          <span style={{ color: '#6b7280' }}>{language === 'ar' ? 'النوع:' : 'Type:'}</span>
                          <span style={{ marginRight: '4px', marginLeft: '4px', fontWeight: '500' }}>
                            {language === 'ar' 
                              ? (task.unit_type === 'apartment' ? 'شقة' : 
                                 task.unit_type === 'villa' ? 'فيلا' : 
                                 task.unit_type === 'office' ? 'مكتب' : 
                                 task.unit_type === 'shop' ? 'محل' : 
                                 task.unit_type === 'warehouse' ? 'مستودع' : 
                                 task.unit_type || 'شقة')
                              : (task.unit_type === 'apartment' ? 'Apt' : 
                                 task.unit_type === 'villa' ? 'Villa' : 
                                 task.unit_type === 'office' ? 'Office' : 
                                 task.unit_type === 'shop' ? 'Shop' : 
                                 task.unit_type === 'warehouse' ? 'Warehouse' : 
                                 task.unit_type || 'Apt')}
                          </span>
                        </div>
                        {task.bedrooms && (
                          <div>
                            <span style={{ color: '#6b7280' }}>{language === 'ar' ? 'غرف:' : 'Beds:'}</span>
                            <span style={{ marginRight: '4px', marginLeft: '4px', fontWeight: '500' }}>{task.bedrooms}</span>
                          </div>
                        )}
                        {task.monthly_rent && (
                          <div>
                            <span style={{ color: '#6b7280' }}>{language === 'ar' ? 'الإيجار:' : 'Rent:'}</span>
                            <span style={{ marginRight: '4px', marginLeft: '4px', fontWeight: '500', color: '#16a34a' }}>
                              {Number(task.monthly_rent).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* SLA Status & Action */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        padding: '10px',
                        background: isOverdue ? '#fef2f2' : isApproaching ? '#fffbeb' : '#f0fdf4',
                        borderRadius: '8px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: isOverdue ? '#dc2626' : isApproaching ? '#d97706' : '#16a34a',
                          fontSize: '0.85rem'
                        }}>
                          <FaClock />
                          {isOverdue ? (
                            <span style={{ fontWeight: '600' }}>
                              {language === 'ar' ? 'تجاوز المهلة!' : 'Deadline passed!'}
                            </span>
                          ) : (
                            <span>
                              {language === 'ar' 
                                ? `${hoursLeft} ساعة متبقية` 
                                : `${hoursLeft} hours left`}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => openListingForm(task)}
                          style={{
                            padding: '8px 16px',
                            background: '#f97316',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <FaEdit />
                          {language === 'ar' ? 'إنشاء الإعلان' : 'Create Listing'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Listing Form Modal */}
        {showListingForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '30px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#1f2937', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaEdit style={{ color: '#f97316' }} />
                  {language === 'ar' ? 'إنشاء إعلان' : 'Create Listing'}
                </h3>
                <button
                  onClick={() => setShowListingForm(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '20px' }}
                >
                  ✕
                </button>
              </div>

              {/* Unit Info Summary */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '8px', 
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                  {language === 'ar' ? 'معلومات الوحدة:' : 'Unit Info:'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div><strong>{language === 'ar' ? 'رقم:' : '#:'}</strong> {showListingForm.unit_number}</div>
                  <div><strong>{language === 'ar' ? 'النوع:' : 'Type:'}</strong> {language === 'ar' 
                    ? (showListingForm.unit_type === 'apartment' ? 'شقة' : 
                       showListingForm.unit_type === 'villa' ? 'فيلا' : 
                       showListingForm.unit_type === 'office' ? 'مكتب' : 
                       showListingForm.unit_type === 'shop' ? 'محل' : 
                       showListingForm.unit_type === 'warehouse' ? 'مستودع' : 
                       showListingForm.unit_type || 'شقة')
                    : showListingForm.unit_type}</div>
                  {showListingForm.bedrooms && <div><strong>{language === 'ar' ? 'غرف:' : 'Beds:'}</strong> {showListingForm.bedrooms}</div>}
                  {showListingForm.monthly_rent && <div><strong>{language === 'ar' ? 'الإيجار:' : 'Rent:'}</strong> {Number(showListingForm.monthly_rent).toLocaleString()}</div>}
                </div>
              </div>
              
              <form onSubmit={handleCreateListing}>
                <div style={{ display: 'grid', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'عنوان الإعلان *' : 'Listing Title *'}
                    </label>
                    <input
                      type="text"
                      value={listingFormData.title}
                      onChange={(e) => setListingFormData({...listingFormData, title: e.target.value})}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'وصف الإعلان' : 'Description'}
                    </label>
                    <textarea
                      value={listingFormData.description}
                      onChange={(e) => setListingFormData({...listingFormData, description: e.target.value})}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box',
                        resize: 'vertical'
                      }}
                      placeholder={language === 'ar' ? 'وصف تفصيلي للوحدة...' : 'Detailed description...'}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                        {language === 'ar' ? 'المنصة *' : 'Platform *'}
                      </label>
                      <select
                        value={listingFormData.platform}
                        onChange={(e) => setListingFormData({...listingFormData, platform: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db'
                        }}
                      >
                        {platforms.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                        {language === 'ar' ? 'السعر (ر.س)' : 'Price (SAR)'}
                      </label>
                      <input
                        type="number"
                        value={listingFormData.price}
                        onChange={(e) => setListingFormData({...listingFormData, price: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'رابط الإعلان على المنصة (اختياري)' : 'Platform ad URL (optional)'}
                    </label>
                    <input
                      type="url"
                      value={listingFormData.external_url}
                      onChange={(e) => setListingFormData({...listingFormData, external_url: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box'
                      }}
                      placeholder="https://haraj.com.sa/..."
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#374151', fontWeight: '500' }}>
                      {language === 'ar' ? 'رقم الإعلان على المنصة (اختياري)' : 'Platform ad number (optional)'}
                    </label>
                    <input
                      type="text"
                      value={listingFormData.platform_ad_number || ''}
                      onChange={(e) => setListingFormData({...listingFormData, platform_ad_number: e.target.value})}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        boxSizing: 'border-box'
                      }}
                      placeholder={language === 'ar' ? 'مثال: 6743016' : 'e.g. 6743016'}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#6b7280' }}>
                      {language === 'ar'
                        ? 'يختلف بين المنصات لنفس الوحدة — ليس رقم ترخيص الهيئة.'
                        : 'Differs per platform for the same unit — not the REGA license.'}
                    </p>
                  </div>
                </div>

                {/* REGA Compliance Section */}
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#fef3c7',
                  borderRadius: '12px',
                  border: '2px solid #fcd34d'
                }}>
                  <h4 style={{ 
                    margin: '0 0 15px 0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    color: '#92400e'
                  }}>
                    🏛️ {language === 'ar' ? 'متطلبات الهيئة العامة للعقار (REGA)' : 'REGA Compliance'}
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'رقم رخصة فال *' : 'FAL License # *'}
                      </label>
                      <input
                        type="text"
                        value={listingFormData.fal_license}
                        onChange={(e) => {
                          setListingFormData({...listingFormData, fal_license: e.target.value});
                          localStorage.setItem('fal_license', e.target.value);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                        placeholder={language === 'ar' ? 'مثال: 1234567890' : 'e.g. 1234567890'}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '0.9rem' }}>
                        {language === 'ar' ? 'رقم ترخيص الإعلان (هيئة العقار) *' : 'REGA ad license # *'}
                      </label>
                      <input
                        type="text"
                        value={listingFormData.ad_license}
                        onChange={(e) => setListingFormData({...listingFormData, ad_license: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          boxSizing: 'border-box'
                        }}
                        placeholder={language === 'ar' ? 'مثال: 7201009770' : 'e.g. 7201009770'}
                      />
                    </div>
                  </div>
                  
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '10px',
                    background: listingFormData.has_brokerage_contract ? '#dcfce7' : 'white',
                    borderRadius: '8px',
                    border: listingFormData.has_brokerage_contract ? '2px solid #16a34a' : '2px solid #e5e7eb'
                  }}>
                    <input
                      type="checkbox"
                      checked={listingFormData.has_brokerage_contract}
                      onChange={(e) => setListingFormData({...listingFormData, has_brokerage_contract: e.target.checked})}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontWeight: '500' }}>
                      {language === 'ar' 
                        ? '✅ أؤكد وجود عقد وساطة ساري مع المالك' 
                        : '✅ I confirm valid brokerage contract with owner'}
                    </span>
                  </label>
                  
                  <p style={{ 
                    margin: '10px 0 0 0', 
                    fontSize: '0.8rem', 
                    color: '#92400e',
                    lineHeight: '1.4'
                  }}>
                    {language === 'ar' 
                      ? '⚠️ يجب إزالة الإعلان خلال يومين من تأجير الوحدة وفقاً لأنظمة الهيئة العامة للعقار'
                      : '⚠️ Ad must be removed within 2 days of rental per REGA regulations'}
                  </p>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  marginTop: '25px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowListingForm(null)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    disabled={submittingListing || !listingFormData.fal_license || !listingFormData.ad_license || !listingFormData.has_brokerage_contract}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: (submittingListing || !listingFormData.fal_license || !listingFormData.ad_license || !listingFormData.has_brokerage_contract) ? '#9ca3af' : '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: submittingListing ? 0.7 : 1
                    }}
                  >
                    <FaCheckCircle />
                    {submittingListing 
                      ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                      : (language === 'ar' ? 'إنشاء ونشر' : 'Create & Post')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Sell & market visibility — funnel, projects, aging (replaces crowded marketing KPI strip) */}
        {isOwnerOrManager && (
          <SellMarketOps language={language} onNavigate={onNavigate} />
        )}

        {/* Secondary stats — denser so the ops panel stays primary */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '20px',
          maxWidth: '900px',
          margin: '0 auto 20px auto'
        }}>
          <div 
            onClick={() => onNavigate && onNavigate('clients')}
            style={{
              padding: '14px',
              background: colors.kpiBackground,
              borderRadius: '12px',
              border: `2px solid ${colors.border}`,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.04) translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
              e.currentTarget.style.borderColor = colors.primary;
              e.currentTarget.style.background = colors.backgroundLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.background = colors.kpiBackground;
            }}
          >
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center', transition: 'transform 0.3s' }}>
              <FaUsers style={{ fontSize: '1.6rem', color: colors.primary, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            </div>
            <div style={{ 
              fontSize: '1.75rem', 
              fontWeight: '800', 
              marginBottom: '4px',
              color: '#1a1a1a',
              transition: 'all 0.3s'
            }}>
              {stats?.totalClients || 0}
            </div>
            <div style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.textSecondary, marginBottom: '8px' }}>{t.totalClients}</div>
            <div style={{ fontSize: '0.75rem', marginTop: '4px', display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span style={{ 
                padding: '3px 8px', 
                background: colors.primary, 
                color: 'white', 
                borderRadius: '6px', 
                fontSize: '0.7rem',
                fontWeight: '600',
                border: '1px solid rgba(249, 115, 22, 0.3)'
              }}>
                {language === 'ar' ? 'عميل محتمل' : 'Prospect'}: {stats?.totalProspects || 0}
              </span>
              <span style={{ 
                padding: '3px 8px', 
                background: colors.primaryDark, 
                color: 'white', 
                borderRadius: '6px', 
                fontSize: '0.7rem',
                fontWeight: '600',
                border: '1px solid rgba(234, 88, 12, 0.3)'
              }}>
                {language === 'ar' ? 'عميل حالي' : 'Customer'}: {stats?.totalCustomers || 0}
              </span>
            </div>
          </div>
          
          <div 
            onClick={() => onNavigate && onNavigate('deals')}
            style={{
              padding: '20px',
              background: colors.kpiBackground,
              borderRadius: '12px',
              border: `3px solid ${colors.border}`,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08) translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
              e.currentTarget.style.borderColor = colors.primary;
              e.currentTarget.style.background = colors.backgroundLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.background = colors.kpiBackground;
            }}
          >
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', transition: 'transform 0.3s' }}>
              <FaHandshake style={{ fontSize: '2.5rem', color: colors.primaryLight, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            </div>
            <div style={{ 
              fontSize: '2.5rem', 
              fontWeight: '800', 
              marginBottom: '6px',
              color: '#1a1a1a',
              transition: 'all 0.3s'
            }}>
              {stats?.totalDeals || 0}
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.textSecondary, marginBottom: '12px' }}>{t.deals || 'Deals'}</div>
            <div style={{ fontSize: '0.85rem', marginTop: '8px', display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
              {(() => {
                const dealsByStatus = stats?.dealsByStatus || [];
                const statusMap = {};
                dealsByStatus.forEach(item => {
                  statusMap[item.status || 'pending'] = parseInt(item.count) || 0;
                });
                const completed = statusMap['completed'] || stats?.completedDeals || 0;
                const inProgress = statusMap['in_progress'] || 0;
                const pending = statusMap['pending'] || 0;
                
                return (
                  <>
                    <span style={{ 
                      padding: '4px 10px', 
                      background: colors.success, 
                      color: 'white', 
                      borderRadius: '6px', 
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      border: '1px solid rgba(249, 115, 22, 0.3)'
                    }}>
                      {language === 'ar' ? 'مكتملة' : 'Completed'}: {completed}
                    </span>
                    <span style={{ 
                      padding: '4px 10px', 
                      background: colors.primary, 
                      color: 'white', 
                      borderRadius: '6px', 
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      border: '1px solid rgba(249, 115, 22, 0.3)'
                    }}>
                      {language === 'ar' ? 'قيد التنفيذ' : 'In Progress'}: {inProgress}
                    </span>
                    <span style={{ 
                      padding: '4px 10px', 
                      background: colors.warning, 
                      color: 'white', 
                      borderRadius: '6px', 
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      border: '1px solid rgba(249, 115, 22, 0.3)'
                    }}>
                      {language === 'ar' ? 'قيد الانتظار' : 'Pending'}: {pending}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
          
          {isOwner ? (
            <div 
              onClick={() => onNavigate && onNavigate('team')}
              style={{
                padding: '20px',
                background: colors.kpiBackground,
                borderRadius: '12px',
                border: `3px solid ${colors.border}`,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: 'scale(1)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.08) translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
                e.currentTarget.style.borderColor = colors.primary;
                e.currentTarget.style.background = colors.backgroundLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = colors.border;
                e.currentTarget.style.background = colors.kpiBackground;
              }}
            >
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', transition: 'transform 0.3s' }}>
                <FaUserFriends style={{ fontSize: '2.5rem', color: colors.primary, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
              </div>
              <div style={{ 
                fontSize: '2.5rem', 
                fontWeight: '800', 
                marginBottom: '6px',
                color: '#1a1a1a',
                transition: 'all 0.3s'
              }}>
                {stats?.totalEmployees || 0}
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.textSecondary }}>
                {language === 'ar' ? 'عدد الموظفين' : 'Total Employees'}
              </div>
            </div>
          ) : (
            <div 
              onClick={handleShowActivities}
              style={{
                padding: '16px',
                background: colors.kpiBackground,
                borderRadius: '8px',
                border: `2px solid ${colors.border}`,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: 'scale(1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                e.currentTarget.style.borderColor = colors.borderHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
                <FaClipboardList style={{ fontSize: '2rem', color: colors.primaryLight }} />
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '6px', color: '#1a1a1a' }}>
                {stats?.recentActivities || 0}
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.textSecondary }}>{t.recentActivities}</div>
            </div>
          )}
          
          <div 
            onClick={handleShowActivities}
            style={{
              padding: '20px',
              background: colors.kpiBackground,
              borderRadius: '12px',
              border: `3px solid ${colors.border}`,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08) translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
              e.currentTarget.style.borderColor = colors.primary;
              e.currentTarget.style.background = colors.backgroundLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.background = colors.kpiBackground;
            }}
          >
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', transition: 'transform 0.3s' }}>
              <FaClipboardList style={{ fontSize: '2.5rem', color: colors.primaryLight, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '6px', color: '#1a1a1a' }}>
              {stats?.recentActivities || 0}
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.textSecondary }}>{t.recentActivities}</div>
          </div>
          
          <div 
            onClick={() => setShowDetails('reminders')}
            style={{
              padding: '20px',
              background: colors.kpiBackground,
              borderRadius: '12px',
              border: `3px solid ${colors.border}`,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: 'scale(1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.08) translateY(-4px)';
              const reminderCount = stats?.upcomingReminders || reminders.length || 0;
              e.currentTarget.style.boxShadow = reminderCount > 0 ? `0 8px 24px ${colors.primary}40` : '0 4px 12px rgba(0,0,0,0.15)';
              e.currentTarget.style.borderColor = reminderCount > 0 ? colors.primary : colors.borderHover;
              e.currentTarget.style.background = colors.backgroundLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = colors.border;
              e.currentTarget.style.background = colors.kpiBackground;
            }}
          >
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center', transition: 'transform 0.3s' }}>
              <FaBell style={{ 
                fontSize: '2.5rem', 
                color: (stats?.upcomingReminders || reminders.length || 0) > 0 ? colors.primary : colors.textMuted,
                filter: (stats?.upcomingReminders || reminders.length || 0) > 0 ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none',
                animation: (stats?.upcomingReminders || reminders.length || 0) > 0 ? 'pulse 2s infinite' : 'none'
              }} />
            </div>
            <div style={{ 
              fontSize: '2.5rem', 
              fontWeight: '800', 
              marginBottom: '6px',
              color: '#1a1a1a',
              transition: 'all 0.3s'
            }}>
              {stats?.upcomingReminders || reminders.length || 0}
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.textSecondary }}>{t.upcomingReminders}</div>
          </div>
          
          {/* Clients by Importance KPI */}
          {(() => {
            const clientsByImportance = stats?.clientsByImportance || stats?.leadsByImportance || [];
            const clientsData = Array.isArray(clientsByImportance) ? clientsByImportance : [];
            const dataMap = {};
            clientsData.forEach(item => {
              const importance = item.importance || item.status || 'medium';
              dataMap[importance] = parseInt(item.count) || 0;
            });
            const highCount = dataMap['high'] || 0;
            const mediumCount = dataMap['medium'] || 0;
            const lowCount = dataMap['low'] || 0;
            const totalByImportance = highCount + mediumCount + lowCount;
            
            return (
              <div 
                onClick={() => onNavigate && onNavigate('clients')}
                style={{
                  padding: '16px',
                  background: colors.kpiBackground,
                  borderRadius: '8px',
                  border: `2px solid ${colors.border}`,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  e.currentTarget.style.borderColor = colors.borderHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = colors.border;
                }}
              >
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
                  <FaFire style={{ fontSize: '1.5rem', color: colors.critical.icon }} />
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', opacity: 0.8 }}>
                    {language === 'ar' ? 'العملاء حسب الأهمية' : 'Clients by Importance'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: colors.critical.bg, borderRadius: '6px', border: `1px solid ${colors.critical.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaFire style={{ fontSize: '0.9rem', color: colors.critical.icon }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.critical.text }}>
                        {language === 'ar' ? 'عالية' : 'High'}
                      </span>
                    </div>
                    <span style={{ fontSize: '1rem', fontWeight: 'bold', color: colors.critical.border }}>
                      {highCount}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: colors.medium.bg, borderRadius: '6px', border: `1px solid ${colors.medium.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaCircle style={{ fontSize: '0.7rem', color: colors.medium.icon }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.medium.text }}>
                        {language === 'ar' ? 'متوسطة' : 'Medium'}
                      </span>
                    </div>
                    <span style={{ fontSize: '1rem', fontWeight: 'bold', color: colors.medium.border }}>
                      {mediumCount}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: colors.high.bg, borderRadius: '6px', border: `1px solid ${colors.high.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FaCircle style={{ fontSize: '0.7rem', color: colors.high.icon }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.high.text }}>
                        {language === 'ar' ? 'منخفضة' : 'Low'}
                      </span>
                    </div>
                    <span style={{ fontSize: '1rem', fontWeight: 'bold', color: colors.high.border }}>
                      {lowCount}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Legacy marketing KPI strip removed — covered by SellMarketOps above */}
        {false && isOwner && (
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            overflow: 'hidden',
            marginBottom: '25px',
            border: '1px solid #fde5d4',
            boxShadow: '0 2px 12px rgba(249, 115, 22, 0.06)'
          }}>
            {/* Section Header - Subtle */}
            <div style={{
              background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #fed7aa'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#9a3412', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '1.15rem',
                fontWeight: '600'
              }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  background: '#f97316',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaChartLine style={{ fontSize: '18px' }} />
                </div>
                {language === 'ar' ? 'عمليات التسويق' : 'Marketing Operations'}
                {loadingMarketing && <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '14px', color: '#f97316' }} />}
              </h3>
              <button
                onClick={() => onNavigate && onNavigate('units')}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: '#ea580c',
                  border: '1px solid #fed7aa',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fff7ed';
                  e.currentTarget.style.borderColor = '#f97316';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#fed7aa';
                }}
              >
                {language === 'ar' ? 'إدارة الوحدات' : 'Manage Units'}
                <FaArrowRight style={{ fontSize: '0.75rem' }} />
              </button>
            </div>
            
            {/* Marketing KPI Cards - Professional Design */}
            <div style={{ padding: '24px' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                gap: '16px',
                marginBottom: '20px'
              }}>
                {/* Vacant Units */}
                <div 
                  style={{
                    padding: '20px',
                    background: '#ffffff',
            borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = '#f97316';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  onClick={() => onNavigate && onNavigate('units')}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: (marketingKpis?.units?.vacant || 0) > 0 ? '#fef2f2' : '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px auto'
                  }}>
                    <FaHome style={{ fontSize: '22px', color: (marketingKpis?.units?.vacant || 0) > 0 ? '#dc2626' : '#64748b' }} />
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                    {marketingKpis?.units?.vacant || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {language === 'ar' ? 'وحدات شاغرة' : 'Vacant Units'}
                  </div>
                </div>
                
                {/* SLA Compliance */}
                <div 
                  style={{
            padding: '20px',
                    background: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = '#f97316';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#fff7ed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px auto'
                  }}>
                    <FaClock style={{ fontSize: '22px', color: '#f97316' }} />
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                    {marketingKpis?.listings?.sla_rate !== null && marketingKpis?.listings?.sla_rate !== undefined ? `${marketingKpis.listings.sla_rate}%` : '-'}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '10px' }}>
                    {language === 'ar' ? 'التزام SLA' : 'SLA Compliance'}
                  </div>
                  {/* Animated progress bar */}
                  <div style={{ 
                    height: '6px', 
                    background: '#f1f5f9', 
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${marketingKpis?.listings?.sla_rate || 0}%`,
                      background: 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)',
                      borderRadius: '3px',
                      transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                    }} />
                  </div>
                </div>
                
                {/* Pending Listings */}
                <div 
                  style={{
                    padding: '20px',
                    background: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = '#f97316';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                  onClick={() => onNavigate && onNavigate('units')}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: (marketingKpis?.listings?.pending || 0) > 0 ? '#fffbeb' : '#f8fafc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px auto'
                  }}>
                    <FaHourglassHalf style={{ fontSize: '20px', color: (marketingKpis?.listings?.pending || 0) > 0 ? '#d97706' : '#64748b' }} />
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '700', color: '#1f2937', marginBottom: '4px' }}>
                    {marketingKpis?.listings?.pending || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {language === 'ar' ? 'قيد الانتظار' : 'Pending'}
                  </div>
                </div>
                
                {/* Active Listings */}
                <div 
                  style={{
                    padding: '20px',
                    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                    borderRadius: '12px',
                    border: '1px solid #fed7aa',
                    cursor: 'pointer',
                    transition: 'all 0.25s ease',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(249, 115, 22, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => onNavigate && onNavigate('units')}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: '#f97316',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px auto'
                  }}>
                    <FaBullhorn style={{ fontSize: '20px', color: 'white' }} />
                  </div>
                  <div style={{ fontSize: '2.2rem', fontWeight: '700', color: '#9a3412', marginBottom: '4px' }}>
                    {marketingKpis?.listings?.active || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#c2410c' }}>
                    {language === 'ar' ? 'إعلانات نشطة' : 'Active Listings'}
                  </div>
                </div>
              </div>

            {/* Pending Marketing Actions - Compact */}
            {pendingMarketingItems.length > 0 && (
              <div style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '5px',
                maxWidth: '500px'
              }}>
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    color: '#374151', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}>
                    <div style={{
                      width: '6px',
                      height: '6px',
                      background: '#f97316',
                      borderRadius: '50%'
                    }} />
                    {language === 'ar' ? 'يتطلب إجراء' : 'Action Required'}
                  </h4>
                  <span style={{
                    background: '#f97316',
                    color: 'white',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {pendingMarketingItems.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                  {pendingMarketingItems.slice(0, 5).map((item, idx) => {
                    const actionLabels = {
                      'sla_breached': { ar: 'متأخر', en: 'Overdue', color: '#dc2626' },
                      'sla_approaching': { ar: 'قريب', en: 'Soon', color: '#d97706' },
                      'needs_closing': { ar: 'إغلاق', en: 'Close', color: '#7c3aed' },
                      'no_listing': { ar: 'جديد', en: 'New', color: '#64748b' },
                      'pending': { ar: 'انتظار', en: 'Wait', color: '#b45309' }
                    };
                    const action = actionLabels[item.action_needed] || { ar: '—', en: '—', color: '#6b7280' };
                    
                    return (
                      <div 
                        key={`${item.id}-${idx}`}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                        }}
                        onClick={() => onNavigate && onNavigate('units')}
                      >
                        <span style={{ 
                          fontWeight: '500', 
                          color: '#1f2937',
                          fontSize: '0.85rem'
                        }}>
                          {item.unit_number}
                        </span>
                        <span style={{
                          padding: '3px 8px',
                          background: 'white',
                          color: action.color,
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          border: `1px solid ${action.color}20`
                        }}>
                          {language === 'ar' ? action.ar : action.en}
                        </span>
                      </div>
                    );
                  })}
                  {pendingMarketingItems.length > 5 && (
                    <button 
                      onClick={() => onNavigate && onNavigate('units')}
                      style={{ 
                        textAlign: 'center', 
                        color: '#f97316', 
                        fontSize: '0.8rem', 
                        padding: '8px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      {language === 'ar' 
                        ? `+${pendingMarketingItems.length - 5} المزيد`
                        : `+${pendingMarketingItems.length - 5} more`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Per-Employee KPIs - Professional */}
            {employeeKpis && employeeKpis.employees && employeeKpis.employees.length > 0 && (
              <div style={{
                marginTop: '20px',
                background: '#ffffff',
                borderRadius: '14px',
                padding: '0',
                border: '1px solid #d6d3d1',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(120, 113, 108, 0.06)'
              }}>
                {/* Section Header - Team (Warm Gray) */}
                <div style={{
                  background: 'linear-gradient(135deg, #f5f5f4 0%, #e7e5e4 100%)',
                  padding: '18px 24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid #d6d3d1'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    color: '#44403c', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    fontSize: '1.15rem',
                    fontWeight: '600'
                  }}>
                    <div style={{
                      width: '38px',
                      height: '38px',
                      background: 'linear-gradient(135deg, #78716c 0%, #57534e 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      <FaUserFriends style={{ fontSize: '18px' }} />
                    </div>
                    {language === 'ar' ? 'أداء فريق التسويق' : 'Marketing Team Performance'}
                    {loadingEmployeeKpis && <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '14px', color: '#78716c' }} />}
                  </h4>
                  <span style={{ fontSize: '0.85rem', color: '#78716c', fontWeight: '500' }}>
                    {employeeKpis.team_totals?.total_employees || 0} {language === 'ar' ? 'موظف' : 'employees'}
                  </span>
                </div>
                
                {/* Team Summary - Professional Cards (Warm Gray Theme) */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '12px', 
                  padding: '16px',
                  background: '#fff'
                }}>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    background: '#fafaf9',
                    borderRadius: '10px',
                    border: '1px solid #e7e5e4'
                  }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#57534e', marginBottom: '2px' }}>
                      {employeeKpis.team_totals?.total_employees || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#78716c', fontWeight: '500' }}>
                      {language === 'ar' ? 'موظفين' : 'Employees'}
                    </div>
                  </div>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    background: '#fafaf9',
                    borderRadius: '10px',
                    border: '1px solid #e7e5e4'
                  }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#57534e', marginBottom: '2px' }}>
                      {employeeKpis.team_totals?.avg_sla_compliance || 0}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#78716c', fontWeight: '500' }}>
                      {language === 'ar' ? 'متوسط الالتزام' : 'Avg SLA'}
                    </div>
                    {/* Mini progress */}
                    <div style={{ 
                      marginTop: '8px', 
                      height: '4px', 
                      background: '#e7e5e4', 
                      borderRadius: '2px'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${employeeKpis.team_totals?.avg_sla_compliance || 0}%`,
                        background: 'linear-gradient(90deg, #78716c, #57534e)',
                        borderRadius: '2px',
                        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                      }} />
                    </div>
                  </div>
                  <div style={{ 
                    textAlign: 'center',
                    padding: '16px 12px',
                    background: '#fafaf9',
                    borderRadius: '10px',
                    border: '1px solid #e7e5e4'
                  }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#57534e', marginBottom: '2px' }}>
                      {employeeKpis.team_totals?.avg_hours_to_post || '-'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#78716c', fontWeight: '500' }}>
                      {language === 'ar' ? 'متوسط الساعات' : 'Avg Hours'}
                    </div>
                  </div>
                </div>

                {/* Employee List - Card Style */}
                <div style={{ padding: '0 20px 20px 20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {employeeKpis.employees.map((emp, idx) => {
                      const slaColor = emp.sla_compliance_rate >= 90 ? '#16a34a' : 
                                      emp.sla_compliance_rate >= 70 ? '#d97706' : '#dc2626';
                      const slaBg = emp.sla_compliance_rate >= 90 ? '#f0fdf4' : 
                                   emp.sla_compliance_rate >= 70 ? '#fffbeb' : '#fef2f2';
                      const performanceLabel = emp.sla_compliance_rate >= 90 
                        ? (language === 'ar' ? 'ممتاز' : 'Excellent')
                        : emp.sla_compliance_rate >= 70 
                          ? (language === 'ar' ? 'جيد' : 'Good')
                          : (language === 'ar' ? 'يحتاج تحسين' : 'Needs Improvement');
                      
                      // Avatar styling matching Team tab - Lighter colors
                      const premiumGradients = [
                        { bg: 'linear-gradient(135deg, #57534e 0%, #78716c 100%)', ring: '#a8a29e' }, // Warm Gray
                        { bg: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)', ring: '#fb923c' }, // Bright Orange
                        { bg: 'linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)', ring: '#fb7185' }, // Rose
                        { bg: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)', ring: '#d8b4fe' }, // Purple
                        { bg: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', ring: '#fbbf24' }, // Amber
                        { bg: 'linear-gradient(135deg, #be123c 0%, #e11d48 100%)', ring: '#fb7185' }, // Burgundy
                        { bg: 'linear-gradient(135deg, #c2410c 0%, #ea580c 100%)', ring: '#fb923c' }, // Terracotta
                        { bg: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)', ring: '#c084fc' }, // Violet
                      ];
                      const colorIndex = emp.employee_name ? 
                        (emp.employee_name.charCodeAt(0) + (emp.employee_name.charCodeAt(1) || 0)) % premiumGradients.length : 0;
                      const avatarStyle = premiumGradients[colorIndex];
                      const initials = emp.employee_name 
                        ? emp.employee_name.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase()
                        : '?';
                      
                      return (
                        <div 
                          key={emp.employee_id} 
                          style={{ 
                            background: 'white',
                            borderRadius: '12px',
                            padding: '15px 18px',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#f97316';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(249, 115, 22, 0.1)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* Employee Info */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Avatar matching Team tab style */}
                            <div style={{
                              position: 'relative',
                              width: '46px',
                              height: '46px'
                            }}>
                              <div style={{
                                position: 'absolute',
                                inset: '-3px',
                                borderRadius: '12px',
                                background: avatarStyle.ring,
                                opacity: 0.3,
                                filter: 'blur(4px)'
                              }} />
                              <div style={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                borderRadius: '12px',
                                background: avatarStyle.bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `2px solid ${avatarStyle.ring}`,
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <span style={{
                                  color: 'white',
                                  fontWeight: '700',
                                  fontSize: '0.95rem',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                  letterSpacing: '0.5px'
                                }}>
                                  {initials}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '0.95rem' }}>
                                {emp.employee_name}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{emp.total_listings || 0} {language === 'ar' ? 'إعلان' : 'listings'}</span>
                                <span>•</span>
                                <span style={{ color: '#f97316', fontWeight: '500' }}>
                                  {emp.listings_this_month || 0} {language === 'ar' ? 'هذا الشهر' : 'this month'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {/* Stats */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            {/* SLA with Progress */}
                            <div style={{ 
                              textAlign: 'center',
                              minWidth: '100px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: '6px',
                                marginBottom: '4px'
                              }}>
                                <span style={{
                                  fontSize: '1.1rem',
                                  fontWeight: '700',
                                  color: slaColor
                                }}>
                                  {emp.sla_compliance_rate !== null ? `${emp.sla_compliance_rate}%` : '-'}
                                </span>
                              </div>
                              <div style={{ 
                                height: '6px', 
                                background: '#e5e7eb', 
                                borderRadius: '3px',
                                overflow: 'hidden',
                                width: '80px'
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${emp.sla_compliance_rate || 0}%`,
                                  background: slaColor,
                                  borderRadius: '3px',
                                  transition: 'width 0.5s ease'
                                }} />
                              </div>
                            </div>
                            
                            {/* Avg Time */}
                            <div style={{ 
                              textAlign: 'center',
                              padding: '6px 12px',
                              background: '#f3f4f6',
                              borderRadius: '8px'
                            }}>
                              <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#374151' }}>
                                {emp.avg_hours_to_post !== null ? `${emp.avg_hours_to_post}h` : '-'}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                {language === 'ar' ? 'متوسط' : 'avg'}
                              </div>
                            </div>
                            
                            {/* Active Badge */}
                            <div style={{
                              padding: '6px 14px',
                              borderRadius: '20px',
                              background: emp.active_listings > 0 ? '#dbeafe' : '#f3f4f6',
                              color: emp.active_listings > 0 ? '#2563eb' : '#9ca3af',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}>
                              <FaBullhorn style={{ fontSize: '11px' }} />
                              {emp.active_listings || 0}
                            </div>
                            
                            {/* Performance Badge */}
                            <span style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              background: slaBg,
                              color: slaColor,
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              border: `1px solid ${slaColor}30`,
                              whiteSpace: 'nowrap'
                            }}>
                              {performanceLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Response SLA Section */}
        {isOwner && responseSla && responseSla.overall.total > 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            overflow: 'hidden',
            marginBottom: '25px',
            border: '1px solid #fcd34d',
            boxShadow: '0 2px 12px rgba(245, 158, 11, 0.08)'
          }}>
            {/* Section Header - Compliance (Amber) */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #fcd34d'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#854d0e', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '1.15rem',
                fontWeight: '600'
              }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaPhone style={{ fontSize: '18px' }} />
                </div>
                {language === 'ar' ? 'مؤشرات وقت الاستجابة (SLA)' : 'Response Time SLA'}
                {loadingResponseSla && <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '14px', color: '#f59e0b' }} />}
              </h3>
            </div>

            {/* Overall SLA (Amber Theme) */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: '15px',
              marginBottom: '20px',
              padding: '20px'
            }}>
              <div style={{
                padding: '15px',
                background: '#fffbeb',
                borderRadius: '10px',
                textAlign: 'center',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                  {responseSla.overall.total}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#78716c' }}>
                  {language === 'ar' ? 'إجمالي الاستجابات' : 'Total Responses'}
                </div>
              </div>
              
              <div style={{
                padding: '15px',
                background: responseSla.overall.sla_rate >= 80 ? '#f0fdf4' : '#fef2f2',
                borderRadius: '10px',
                textAlign: 'center',
                border: `1px solid ${responseSla.overall.sla_rate >= 80 ? '#86efac' : '#fca5a5'}`
              }}>
                <div style={{ 
                  fontSize: '2rem', 
                  fontWeight: 'bold', 
                  color: responseSla.overall.sla_rate >= 80 ? '#16a34a' : '#dc2626' 
                }}>
                  {responseSla.overall.sla_rate}%
                </div>
                <div style={{ fontSize: '0.8rem', color: '#78716c' }}>
                  {language === 'ar' ? 'نسبة الالتزام' : 'SLA Compliance'}
                </div>
              </div>
              
              <div style={{
                padding: '15px',
                background: '#fffbeb',
                borderRadius: '10px',
                textAlign: 'center',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#d97706' }}>
                  {responseSla.overall.avg_response_seconds 
                    ? responseSla.overall.avg_response_seconds < 60 
                      ? `${Math.round(responseSla.overall.avg_response_seconds)}s`
                      : `${Math.round(responseSla.overall.avg_response_seconds / 60)}m`
                    : '-'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#78716c' }}>
                  {language === 'ar' ? 'متوسط الاستجابة' : 'Avg Response'}
                </div>
              </div>
            </div>

            {/* By Channel */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px'
            }}>
              {/* Phone */}
              {responseSla.by_channel.phone.total > 0 && (
                <div style={{
                  padding: '15px',
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FaPhone style={{ color: '#16a34a' }} />
                    <strong>{language === 'ar' ? 'المكالمات' : 'Phone'}</strong>
                    <span style={{ 
                      marginRight: 'auto',
                      padding: '2px 8px',
                      background: responseSla.by_channel.phone.sla_rate >= 80 ? '#dcfce7' : '#fee2e2',
                      color: responseSla.by_channel.phone.sla_rate >= 80 ? '#16a34a' : '#dc2626',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {responseSla.by_channel.phone.sla_rate}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {responseSla.by_channel.phone.total} {language === 'ar' ? 'مكالمة' : 'calls'} • 
                    {language === 'ar' ? ' الهدف: ' : ' Target: '}{responseSla.by_channel.phone.target_seconds}s
                  </div>
                </div>
              )}
              
              {/* WhatsApp */}
              {responseSla.by_channel.whatsapp.total > 0 && (
                <div style={{
                  padding: '15px',
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FaWhatsapp style={{ color: '#25d366' }} />
                    <strong>{language === 'ar' ? 'واتساب' : 'WhatsApp'}</strong>
                    <span style={{ 
                      marginRight: 'auto',
                      padding: '2px 8px',
                      background: responseSla.by_channel.whatsapp.sla_rate >= 80 ? '#dcfce7' : '#fee2e2',
                      color: responseSla.by_channel.whatsapp.sla_rate >= 80 ? '#16a34a' : '#dc2626',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {responseSla.by_channel.whatsapp.sla_rate}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {responseSla.by_channel.whatsapp.total} {language === 'ar' ? 'رسالة' : 'messages'} • 
                    {language === 'ar' ? ' الهدف: ' : ' Target: '}{Math.round(responseSla.by_channel.whatsapp.target_seconds / 60)}m
                  </div>
                </div>
              )}
              
              {/* Email */}
              {responseSla.by_channel.email.total > 0 && (
                <div style={{
                  padding: '15px',
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <FaEnvelope style={{ color: '#6366f1' }} />
                    <strong>{language === 'ar' ? 'البريد' : 'Email'}</strong>
                    <span style={{ 
                      marginRight: 'auto',
                      padding: '2px 8px',
                      background: responseSla.by_channel.email.sla_rate >= 80 ? '#dcfce7' : '#fee2e2',
                      color: responseSla.by_channel.email.sla_rate >= 80 ? '#16a34a' : '#dc2626',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {responseSla.by_channel.email.sla_rate}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {responseSla.by_channel.email.total} {language === 'ar' ? 'بريد' : 'emails'} • 
                    {language === 'ar' ? ' الهدف: ' : ' Target: '}{Math.round(responseSla.by_channel.email.target_seconds / 60)}m
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Automated Reports Section */}
        {isOwner && (
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            overflow: 'hidden',
            marginBottom: '25px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 12px rgba(107, 114, 128, 0.06)'
          }}>
            {/* Section Header - Reports (Cool Neutral) */}
            <div style={{
              background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#374151', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '1.15rem',
                fontWeight: '600'
              }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaFileAlt style={{ fontSize: '18px' }} />
                </div>
                {language === 'ar' ? 'التقارير التلقائية' : 'Automated Reports'}
              </h3>
            </div>
            
            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', padding: '20px 20px 0 20px' }}>
              <button
                onClick={() => loadDailyReport()}
                disabled={loadingReports}
                style={{
                  padding: '10px 20px',
                  background: showReport === 'daily' ? '#6b7280' : '#f9fafb',
                  color: showReport === 'daily' ? 'white' : '#374151',
                  border: '2px solid #9ca3af',
                  borderRadius: '8px',
                  cursor: loadingReports ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  opacity: loadingReports ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {language === 'ar' ? '📋 التقرير اليومي' : '📋 Daily Report'}
              </button>
              
                  <button
                    onClick={() => loadWeeklyReport(language)}
                    disabled={loadingReports}
                    style={{
                  padding: '10px 20px',
                  background: showReport === 'weekly' ? '#6b7280' : '#f9fafb',
                  color: showReport === 'weekly' ? 'white' : '#374151',
                  border: '2px solid #9ca3af',
                  borderRadius: '8px',
                  cursor: loadingReports ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  opacity: loadingReports ? 0.6 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {language === 'ar' ? '📊 التقرير الأسبوعي' : '📊 Weekly Report'}
              </button>
            </div>

            {loadingReports && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                {language === 'ar' ? 'جاري تحميل التقرير...' : 'Loading report...'}
              </div>
            )}

            {showReport === 'daily' && dailyReport && (
              <div style={{
                background: colors.backgroundLight,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '15px',
                marginTop: '15px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: colors.primary }}>
                  {language === 'ar' ? '📋 التقرير اليومي' : '📋 Daily Report'}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'التذكيرات اليوم' : 'Reminders Today'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.primary }}>
                      {dailyReport.summary?.total_reminders_today || 0}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'الأولويات' : 'Priorities'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.primary }}>
                      {dailyReport.summary?.total_priorities || 0}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'متأخرة' : 'Overdue'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.warning }}>
                      {dailyReport.summary?.overdue_count || 0}
                    </div>
                  </div>
                </div>
                {dailyReport.critical_priorities && dailyReport.critical_priorities.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: colors.critical.border }}>
                      {language === 'ar' ? '🔥 أولويات حرجة:' : '🔥 Critical Priorities:'}
                    </strong>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {dailyReport.critical_priorities.slice(0, 3).map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '5px', fontSize: '0.9rem' }}>
                          {item.title} {item.value && `(${item.value.toLocaleString()} SAR)`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {showReport === 'weekly' && weeklyReport && (
              <div style={{
                background: colors.backgroundLight,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                padding: '15px',
                marginTop: '15px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: colors.primary }}>
                  {language === 'ar' ? '📊 التقرير الأسبوعي' : '📊 Weekly Report'}
                </h4>
                
                {/* Week Info */}
                {weeklyReport.week_start && (
                  <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '15px' }}>
                    {language === 'ar' 
                      ? `الأسبوع: ${new Date(weeklyReport.week_start).toLocaleDateString('ar-SA')} - ${new Date(weeklyReport.week_end).toLocaleDateString('ar-SA')}`
                      : `Week: ${new Date(weeklyReport.week_start).toLocaleDateString()} - ${new Date(weeklyReport.week_end).toLocaleDateString()}`
                    }
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'معدل الإنجاز' : 'Completion Rate'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.primary }}>
                      {weeklyReport.performance?.completion_rate || 0}%
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'مكتملة' : 'Completed'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.success }}>
                      {weeklyReport.performance?.total_completed || 0}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'معلقة' : 'Pending'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.warning }}>
                      {weeklyReport.pending_items?.total || 0}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: colors.kpiBackground, borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: '5px' }}>
                      {language === 'ar' ? 'المتوقع الأسبوع القادم' : 'Next Week Expected'}
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.info }}>
                      {weeklyReport.next_week_preview?.expected_reminders || 0}
                    </div>
                  </div>
                </div>

                {/* Show message if no data */}
                {(!weeklyReport.performance || (weeklyReport.performance.total_created === 0 && weeklyReport.performance.total_completed === 0)) && (
                  <div style={{ 
                    padding: '20px', 
                    background: colors.secondary, 
                    borderRadius: '6px', 
                    textAlign: 'center',
                    color: colors.textMuted,
                    marginBottom: '15px'
                  }}>
                    {language === 'ar' 
                      ? 'لا توجد بيانات لهذا الأسبوع بعد. سيظهر التقرير عند إنشاء التذكيرات وإتمام المهام.'
                      : 'No activity data for this week yet. Reports will populate as you create reminders and complete tasks.'
                    }
                  </div>
                )}

                {/* Insights and Recommendations */}
                {weeklyReport.insights && weeklyReport.insights.recommendations && weeklyReport.insights.recommendations.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: colors.text }}>
                      {language === 'ar' ? '💡 التوصيات:' : '💡 Recommendations:'}
                    </strong>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {weeklyReport.insights.recommendations.slice(0, 5).map((rec, idx) => (
                        <li key={idx} style={{ marginBottom: '5px', fontSize: '0.9rem' }}>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Pending Items List */}
                {weeklyReport.pending_items && weeklyReport.pending_items.items && weeklyReport.pending_items.items.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong style={{ color: colors.text }}>
                      {language === 'ar' ? '📋 المهام المعلقة:' : '📋 Pending Items:'}
                    </strong>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {weeklyReport.pending_items.items.slice(0, 5).map((item, idx) => (
                        <li key={idx} style={{ marginBottom: '5px', fontSize: '0.9rem' }}>
                          {translateReminderTitle(item.title, language)}
                          {item.days_overdue > 0 && (
                            <span style={{ color: colors.warning, marginLeft: '8px' }}>
                              {language === 'ar' ? `- متأخر ${item.days_overdue} يوم` : `- ${item.days_overdue} days overdue`}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {!showReport && !loadingReports && (
              <div style={{ textAlign: 'center', padding: '20px', color: colors.textMuted, fontSize: '0.9rem' }}>
                {language === 'ar' 
                  ? 'انقر على أحد الأزرار أعلاه لعرض التقرير اليومي أو الأسبوعي' 
                  : 'Click one of the buttons above to view daily or weekly report'}
              </div>
            )}
          </div>
        )}

        {/* Priority Management Section - Owner Only */}
        {isOwner && (
          <div style={{ 
            background: '#ffffff',
            borderRadius: '14px',
            overflow: 'hidden',
            marginBottom: '25px',
            border: '1px solid #fb923c',
            boxShadow: '0 2px 12px rgba(234, 88, 12, 0.08)'
          }}>
            {/* Section Header - Analytics (Terracotta) */}
            <div style={{
              background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #fb923c'
            }}>
              <h2 style={{ 
                margin: 0, 
                color: '#7c2d12', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                fontSize: '1.15rem',
                fontWeight: '600'
              }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white'
                }}>
                  <FaExclamationCircle style={{ fontSize: '18px' }} />
                </div>
                {language === 'ar' ? 'إدارة الأولويات' : 'Priority Management'}
              </h2>
              
              {/* Priority Calculation Explanation - Collapsible */}
              <details className="priority-explanation" style={{ 
                padding: '0',
                background: '#fffbeb',
                borderRadius: '8px',
                border: '2px solid #fdba74',
                fontSize: '0.8rem',
                maxWidth: '400px',
                lineHeight: '1.5',
                transition: 'all 0.3s ease',
                overflow: 'hidden'
              }}>
                <summary style={{ 
                  fontWeight: '600', 
                  color: '#7c2d12', 
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  listStyle: 'none',
                  userSelect: 'none',
                  padding: '14px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '10px',
                  background: '#fffbeb',
                  transition: 'all 0.2s ease',
                  border: 'none',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fed7aa';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fffbeb';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FaCalculator style={{ fontSize: '1.2rem', color: '#c2410c' }} />
                    <span style={{ fontWeight: '700' }}>{language === 'ar' ? 'حساب الأولوية' : 'Priority Calculation'}</span>
                  </span>
                  <FaChevronDown style={{ 
                    fontSize: '0.9rem', 
                    color: '#c2410c',
                    transition: 'transform 0.3s ease'
                  }} 
                  className="chevron-icon" />
                </summary>
                <div style={{ 
                  marginTop: '0', 
                  padding: '12px 16px', 
                  borderTop: '2px solid #fdba74',
                  background: '#fff7ed'
                }}>
                  <div style={{ color: '#9a3412', fontSize: '0.85rem', lineHeight: '1.6' }}>
                    {language === 'ar' 
                      ? '• السعر (40%) • الاستعجال (40%) • التقدم (20%)'
                      : '• Price (40%) • Urgency (40%) • Progress (20%)'}
                  </div>
                </div>
              </details>
              <style>{`
                details[open] .chevron-icon {
                  transform: rotate(180deg);
                }
                details summary::-webkit-details-marker {
                  display: none;
                }
                @keyframes pulse {
                  0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                  }
                  50% {
                    opacity: 0.9;
                    transform: scale(1.02);
                  }
                }
              `}</style>
            </div>
            
            <div style={{ padding: '20px' }}>
            {loadingPriorities ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FaSync style={{ fontSize: '2rem', color: '#ea580c', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '10px', color: '#7c2d12' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading priorities...'}</p>
              </div>
            ) : priorityData ? (
              <>
                {/* Priority Summary Cards (Terracotta Theme) */}
                <div className="priority-summary-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '15px',
                  marginBottom: '30px'
                }}>
                  {/* Critical Card */}
                  <div className="priority-card" style={{
                    padding: '18px',
                    background: colors.critical.bg,
                    borderRadius: '12px',
                    border: `3px solid ${colors.critical.border}`,
                    textAlign: 'center',
                    boxShadow: (priorityData.summary?.critical || 0) > 0 ? `0 4px 16px ${colors.critical.border}50` : '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: 'scale(1)',
                    position: 'relative',
                    animation: (priorityData.summary?.critical || 0) > 0 ? 'pulse 2s infinite' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${colors.critical.border}70`;
                    e.currentTarget.style.borderWidth = '4px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = (priorityData.summary?.critical || 0) > 0 ? `0 4px 16px ${colors.critical.border}50` : '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderWidth = '3px';
                  }}>
                    <FaExclamationCircle className="priority-icon" style={{ 
                      fontSize: '2rem', 
                      color: colors.critical.icon, 
                      marginBottom: '10px',
                      filter: (priorityData.summary?.critical || 0) > 0 ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
                    }} />
                    <div className="priority-number" style={{ 
                      fontSize: '2.5rem', 
                      fontWeight: '900', 
                      color: colors.critical.border,
                      textShadow: (priorityData.summary?.critical || 0) > 0 ? `0 2px 8px ${colors.critical.border}50` : 'none',
                      transition: 'all 0.3s'
                    }}>
                      {priorityData.summary?.critical || 0}
                    </div>
                    <div className="priority-label" style={{ fontSize: '0.9rem', color: colors.critical.text, fontWeight: '700', marginTop: '4px' }}>
                      {language === 'ar' ? '🔴 حرج' : '🔴 Critical'}
                    </div>
                  </div>
                  
                  {/* High Card */}
                  <div className="priority-card" style={{
                    padding: '18px',
                    background: colors.high.bg,
                    borderRadius: '12px',
                    border: `3px solid ${colors.high.border}`,
                    textAlign: 'center',
                    boxShadow: (priorityData.summary?.high || 0) > 0 ? `0 4px 16px ${colors.high.border}40` : '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: 'scale(1)',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1) translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 8px 24px ${colors.high.border}60`;
                    e.currentTarget.style.borderWidth = '4px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = (priorityData.summary?.high || 0) > 0 ? `0 4px 16px ${colors.high.border}40` : '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderWidth = '3px';
                  }}>
                    <FaArrowUp className="priority-icon" style={{ 
                      fontSize: '2rem', 
                      color: colors.high.icon, 
                      marginBottom: '10px',
                      filter: (priorityData.summary?.high || 0) > 0 ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'
                    }} />
                    <div className="priority-number" style={{ 
                      fontSize: '2.5rem', 
                      fontWeight: '900', 
                      color: colors.high.border,
                      textShadow: (priorityData.summary?.high || 0) > 0 ? `0 2px 8px ${colors.high.border}40` : 'none',
                      transition: 'all 0.3s'
                    }}>
                      {priorityData.summary?.high || 0}
                    </div>
                    <div className="priority-label" style={{ fontSize: '0.9rem', color: colors.high.text, fontWeight: '700', marginTop: '4px' }}>
                      {language === 'ar' ? '🟠 عالي' : '🟠 High'}
                    </div>
                  </div>
                  
                  {/* Medium Card */}
                  <div className="priority-card" style={{
                    padding: '18px',
                    background: colors.medium.bg,
                    borderRadius: '12px',
                    border: `2px solid ${colors.medium.border}`,
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderWidth = '3px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderWidth = '2px';
                  }}>
                    <FaMinus className="priority-icon" style={{ fontSize: '1.8rem', color: colors.medium.icon, marginBottom: '10px' }} />
                    <div className="priority-number" style={{ fontSize: '2rem', fontWeight: '800', color: colors.medium.border }}>
                      {priorityData.summary?.medium || 0}
                    </div>
                    <div className="priority-label" style={{ fontSize: '0.85rem', color: colors.medium.text, fontWeight: '600', marginTop: '4px' }}>
                      {language === 'ar' ? '🟡 متوسط' : '🟡 Medium'}
                    </div>
                  </div>
                  
                  {/* Low Card */}
                  <div className="priority-card" style={{
                    padding: '18px',
                    background: colors.low.bg,
                    borderRadius: '12px',
                    border: `2px solid ${colors.low.border}`,
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(229, 213, 184, 0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderWidth = '3px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(229, 213, 184, 0.2)';
                    e.currentTarget.style.borderWidth = '2px';
                  }}>
                    <FaArrowDown className="priority-icon" style={{ fontSize: '1.8rem', color: colors.low.icon, marginBottom: '10px' }} />
                    <div className="priority-number" style={{ fontSize: '2rem', fontWeight: '800', color: colors.low.border }}>
                      {priorityData.summary?.low || 0}
                    </div>
                    <div className="priority-label" style={{ fontSize: '0.85rem', color: colors.low.text, fontWeight: '600', marginTop: '4px' }}>
                      {language === 'ar' ? '⚪ منخفض' : '⚪ Low'}
                    </div>
                  </div>
                  
                  {/* Overdue Card - Always Show if exists */}
                  <div className="priority-card" style={{
                    padding: '15px',
                    background: priorityData.summary?.overdue > 0 ? colors.critical.bg : colors.neutral,
                    borderRadius: '8px',
                    border: `2px solid ${priorityData.summary?.overdue > 0 ? colors.critical.border : colors.border}`,
                    textAlign: 'center',
                    animation: priorityData.summary?.overdue > 0 ? 'pulse 2s infinite' : 'none',
                    boxShadow: priorityData.summary?.overdue > 0 ? '0 2px 8px rgba(220, 38, 38, 0.3)' : 'none'
                  }}>
                    <FaClock className="priority-icon" style={{ fontSize: '1.5rem', color: priorityData.summary?.overdue > 0 ? colors.critical.icon : colors.low.icon, marginBottom: '8px' }} />
                    <div className="priority-number" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: priorityData.summary?.overdue > 0 ? colors.critical.border : colors.textSecondary }}>
                      {priorityData.summary?.overdue || 0}
                    </div>
                    <div className="priority-label" style={{ fontSize: '0.85rem', color: priorityData.summary?.overdue > 0 ? colors.critical.text : colors.textSecondary, fontWeight: '600' }}>
                      {language === 'ar' ? '⏰ متأخر' : '⏰ Overdue'}
                    </div>
                  </div>
                </div>

                {/* Top Priorities (All priorities sorted by score) */}
                {(() => {
                  // Combine ALL priority items (critical, high, medium, low), sorted by score
                  const allPriorities = [
                    ...(priorityData.priorities?.critical || []),
                    ...(priorityData.priorities?.high || []),
                    ...(priorityData.priorities?.medium || []),
                    ...(priorityData.priorities?.low || [])
                  ]
                    .sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0))
                    .slice(0, 15); // Show top 15 items
                  
                  return allPriorities.length > 0 ? (
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaExclamationCircle style={{ color: colors.primary }} />
                        {language === 'ar' ? 'أولويات عالية' : 'High Priorities'}
                      </h4>
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {allPriorities.map((item, index) => {
                        const priorityColors = {
                          critical: colors.critical,
                          high: colors.high,
                          medium: colors.medium,
                          low: colors.low
                        };
                        const itemColors = priorityColors[item.priority_level] || priorityColors.medium;
                        const itemIcon = item.item_type === 'deal' ? <FaHandshake /> : 
                                        item.item_type === 'reminder' ? <FaBell /> : 
                                        <FaClipboardList />;
                        
                        return (
                          <div
                            key={`${item.item_type}-${item.id}`}
                            style={{
                              padding: '8px 12px',
                              background: '#ffffff',
                              borderLeft: `3px solid ${itemColors.border}`,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateX(3px)';
                              e.currentTarget.style.boxShadow = `0 2px 8px ${itemColors.border}30`;
                              e.currentTarget.style.background = itemColors.bg;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              e.currentTarget.style.background = '#ffffff';
                            }}
                            onClick={() => {
                              if (item.item_type === 'deal' && onNavigate) {
                                onNavigate('deals');
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                              <div style={{ fontSize: '1.2rem', color: itemColors.icon }}>
                                {itemIcon}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px', color: itemColors.text }}>
                                  {item.title || item.property_address || item.contact_name || 'Untitled'}
                                </div>
                                <div style={{ fontSize: '0.85rem', opacity: 0.7, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>
                                      {item.item_type === 'deal' && language === 'ar' ? 'صفقة' : item.item_type === 'deal' ? 'Deal' :
                                       item.item_type === 'reminder' && language === 'ar' ? 'تذكير' : item.item_type === 'reminder' ? 'Reminder' :
                                       language === 'ar' ? 'مهمة' : 'Task'}
                                    </span>
                                    {item.priority_level && (
                                      <span style={{
                                        padding: '2px 8px',
                                        background: itemColors.bg,
                                        border: `1px solid ${itemColors.border}`,
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        color: itemColors.text
                                      }}>
                                        {item.priority_level === 'critical' && (language === 'ar' ? 'حرج' : 'Critical')}
                                        {item.priority_level === 'high' && (language === 'ar' ? 'عالي' : 'High')}
                                        {item.priority_level === 'medium' && (language === 'ar' ? 'متوسط' : 'Medium')}
                                        {item.priority_level === 'low' && (language === 'ar' ? 'منخفض' : 'Low')}
                                      </span>
                                    )}
                                  </div>
                                  {item.days_until !== undefined && (
                                    <div style={{ fontSize: '0.8rem' }}>
                                      {item.days_until < 0 
                                        ? `${language === 'ar' ? '⏰ متأخر' : '⏰ Overdue'}`
                                        : item.days_until === 0
                                        ? `${language === 'ar' ? '⏰ اليوم' : '⏰ Today'}`
                                        : `${language === 'ar' ? '⏰' : '⏰'} ${item.days_until} ${language === 'ar' ? 'أيام متبقية' : 'days left'}`
                                      }
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  ) : null;
                })()}
                
                {/* Today's Priorities (if any) */}
                {priorityData.today && priorityData.today.length > 0 && (
                  <div style={{ marginBottom: '30px' }}>
                    <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaClock style={{ color: colors.primary }} />
                      {language === 'ar' ? 'أولويات اليوم' : "Today's Priorities"}
                    </h4>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {priorityData.today.slice(0, 5).map((item, index) => {
                        const priorityColors = {
                          critical: colors.critical,
                          high: colors.high,
                          medium: colors.medium,
                          low: colors.low
                        };
                        const itemColors = priorityColors[item.priority_level] || priorityColors.medium;
                        const itemIcon = item.item_type === 'deal' ? <FaHandshake /> : 
                                        item.item_type === 'reminder' ? <FaBell /> : 
                                        <FaClipboardList />;
                        
                        return (
                          <div
                            key={`today-${item.item_type}-${item.id}`}
                            style={{
                              padding: '8px 12px',
                              background: '#ffffff',
                              borderLeft: `3px solid ${itemColors.border}`,
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateX(3px)';
                              e.currentTarget.style.boxShadow = `0 2px 8px ${itemColors.border}30`;
                              e.currentTarget.style.background = itemColors.bg;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                              e.currentTarget.style.background = '#ffffff';
                            }}
                            onClick={() => {
                              if (item.item_type === 'deal' && onNavigate) {
                                onNavigate('deals');
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                              <div style={{ fontSize: '1.2rem', color: itemColors.icon }}>
                                {itemIcon}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px', color: itemColors.text }}>
                                  {item.title || item.property_address || item.contact_name || 'Untitled'}
                                </div>
                                <div style={{ fontSize: '0.85rem', opacity: 0.7, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>
                                      {item.item_type === 'deal' && language === 'ar' ? 'صفقة' : item.item_type === 'deal' ? 'Deal' :
                                       item.item_type === 'reminder' && language === 'ar' ? 'تذكير' : item.item_type === 'reminder' ? 'Reminder' :
                                       language === 'ar' ? 'مهمة' : 'Task'}
                                    </span>
                                    {item.priority_level && (
                                      <span style={{
                                        padding: '2px 8px',
                                        background: itemColors.bg,
                                        border: `1px solid ${itemColors.border}`,
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        color: itemColors.text
                                      }}>
                                        {item.priority_level === 'critical' && (language === 'ar' ? 'حرج' : 'Critical')}
                                        {item.priority_level === 'high' && (language === 'ar' ? 'عالي' : 'High')}
                                        {item.priority_level === 'medium' && (language === 'ar' ? 'متوسط' : 'Medium')}
                                        {item.priority_level === 'low' && (language === 'ar' ? 'منخفض' : 'Low')}
                                      </span>
                                    )}
                                  </div>
                                  {item.days_until !== undefined && (
                                    <div style={{ fontSize: '0.8rem' }}>
                                      {item.days_until < 0 
                                        ? `${language === 'ar' ? '⏰ متأخر' : '⏰ Overdue'}`
                                        : item.days_until === 0
                                        ? `${language === 'ar' ? '⏰ اليوم' : '⏰ Today'}`
                                        : `${language === 'ar' ? '⏰' : '⏰'} ${item.days_until} ${language === 'ar' ? 'أيام متبقية' : 'days left'}`
                                      }
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: '#fffbeb', borderRadius: '8px' }}>
                <FaExclamationCircle style={{ fontSize: '2rem', color: '#78716c', marginBottom: '10px' }} />
                <p style={{ opacity: 0.6, marginBottom: '10px', color: '#7c2d12' }}>
                  {language === 'ar' ? 'لا توجد أولويات حالياً' : 'No priorities at the moment'}
                </p>
                <p style={{ fontSize: '0.85rem', opacity: 0.5, color: '#78716c' }}>
                  {language === 'ar' 
                    ? 'أضف صفقات مع سعر وتاريخ استحقاق لرؤية الأولويات'
                    : 'Add deals with price and due date to see priorities'}
                </p>
              </div>
            )}
            </div>
          </div>
        )}


        {/* Details Modal */}
        {showDetails === 'reminders' && (
          <div className="modal-overlay" onClick={() => setShowDetails(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>{t.upcomingReminders}</h2>
                <button className="close-btn" onClick={() => setShowDetails(null)}>×</button>
              </div>
              <div style={{ padding: '20px' }}>
                {reminders.length === 0 ? (
                  <p>{t.noReminders}</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {reminders.map((reminder) => (
                      <div key={reminder.id} style={{
                        padding: '15px',
                        background: colors.neutralLight,
                        borderRadius: '8px',
                        borderLeft: `4px solid ${reminder.is_auto ? colors.primary : colors.border}`,
                        position: 'relative'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <strong>{reminder.title}</strong>
                          {reminder.is_auto && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '0.75rem',
                              padding: '2px 8px',
                              background: colors.primary + '20',
                              color: colors.primary,
                              borderRadius: '12px',
                              fontWeight: '500'
                            }} title={language === 'ar' ? 'تذكير تلقائي' : 'Auto-generated'}>
                              <FaRobot style={{ fontSize: '0.7rem' }} />
                              {language === 'ar' ? 'تلقائي' : 'Auto'}
                            </span>
                          )}
                        </div>
                        <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', opacity: 0.7 }}>
                          {reminder.contact_name} - {new Date(reminder.reminder_date).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showDetails === 'activities' && (
          <div className="modal-overlay" onClick={() => setShowDetails(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="modal-header">
                <h2>{t.recentActivities} - {t.summary || 'Summary'}</h2>
                <button className="close-btn" onClick={() => setShowDetails(null)}>×</button>
              </div>
              <div style={{ padding: '20px' }}>
                {loadingActivities ? (
                  <p>{t.loading}</p>
                ) : recentActivities.length === 0 ? (
                  <div className="empty-state">
                    <p>{t.noActivities || 'No recent activities found.'}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ 
                      marginBottom: '10px', 
                      padding: '10px', 
                      background: colors.background, 
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      opacity: 0.8
                    }}>
                      {(t.recentActivitiesSummary || 'Showing the last {count} activities across all contacts.').replace('{count}', recentActivities.length)}
                    </div>
                    {recentActivities.map((activity) => (
                      <div key={activity.id} style={{
                        padding: '15px',
                        background: colors.neutralLight,
                        borderRadius: '8px',
                        borderLeft: `4px solid ${colors.border}`,
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = colors.background;
                        e.currentTarget.style.borderLeftColor = colors.borderHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = colors.neutralLight;
                        e.currentTarget.style.borderLeftColor = colors.border;
                      }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                          <span style={{ fontSize: '1.8rem', flexShrink: 0 }}>
                            {getActivityIcon(activity.activity_type)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                              <strong style={{ fontSize: '1rem' }}>
                                {activity.title || t[activity.activity_type] || activity.activity_type}
                              </strong>
                              <span style={{ 
                                fontSize: '0.75rem', 
                                padding: '2px 8px', 
                                background: activity.contact_type === 'client' ? colors.border : colors.primary + '20',
                                borderRadius: '12px',
                                fontWeight: '500'
                              }}>
                                {activity.contact_type === 'client' ? t.clients : t.leads}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.9rem', marginBottom: '4px', fontWeight: '500', color: colors.text }}>
                              {activity.contact_name || t.unknownContact || 'Unknown Contact'}
                            </div>
                            {activity.description && (
                              <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', color: colors.textSecondary, lineHeight: '1.4' }}>
                                {activity.description}
                              </p>
                            )}
                            <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', opacity: 0.6 }}>
                              {new Date(activity.created_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Reminders Preview - Improved with Urgency */}
        {reminders.length > 0 && (() => {
          const groupedReminders = groupRemindersByUrgency(reminders);
          const hasOverdue = groupedReminders.overdue.length > 0;
          const hasToday = groupedReminders.today.length > 0;
          const hasWeek = groupedReminders.week.length > 0;
          
          return (
            <div style={{ 
              marginTop: '30px',
              background: '#ffffff',
              borderRadius: '14px',
              overflow: 'hidden',
              border: hasOverdue ? '2px solid #dc2626' : '1px solid #e5e7eb',
              boxShadow: hasOverdue ? '0 4px 20px rgba(220, 38, 38, 0.15)' : '0 2px 12px rgba(0,0,0,0.06)'
            }}>
              {/* Header */}
              <div style={{
                background: hasOverdue 
                  ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' 
                  : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                padding: '18px 24px',
                borderBottom: hasOverdue ? '2px solid #dc2626' : '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ 
                    margin: 0,
                    color: hasOverdue ? '#7f1d1d' : '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontSize: '1.15rem',
                    fontWeight: '600'
                  }}>
                    <div style={{
                      width: '38px',
                      height: '38px',
                      background: hasOverdue 
                        ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' 
                        : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      animation: hasOverdue ? 'pulse 2s infinite' : 'none'
                    }}>
                      <FaBell style={{ fontSize: '18px' }} />
                    </div>
                    {t.upcomingReminders}
                  </h3>
                  <span style={{ 
                    fontSize: '0.85rem',
                    color: hasOverdue ? '#dc2626' : '#6b7280',
                    fontWeight: '600'
                  }}>
                    {reminders.length} {language === 'ar' ? 'تذكير' : 'reminders'}
                  </span>
                </div>
              </div>

              <div style={{ padding: '20px' }}>
                {/* Overdue Reminders */}
                {hasOverdue && (
                  <div style={{ marginBottom: hasToday || hasWeek ? '24px' : '0' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '12px',
                      padding: '8px 12px',
                      background: '#fef2f2',
                      borderRadius: '8px',
                      border: '1px solid #fca5a5'
                    }}>
                      <span style={{ fontSize: '1rem' }}>🔴</span>
                      <span style={{ 
                        fontWeight: '700', 
                        color: '#7f1d1d',
                        fontSize: '0.95rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {language === 'ar' ? 'متأخر' : 'OVERDUE'} ({groupedReminders.overdue.length})
                      </span>
                    </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {groupedReminders.overdue.map((reminder) => (
                <div key={reminder.id} style={{
                          padding: '16px',
                          background: reminder.urgency.bg,
                          borderRadius: '10px',
                          border: `2px solid ${reminder.urgency.border}`,
                          position: 'relative',
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#1f2937', fontSize: '0.95rem' }}>{reminder.title}</strong>
                    {reminder.is_auto && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                                    fontSize: '0.7rem',
                                    padding: '2px 8px',
                                    background: colors.primary + '20',
                                    color: colors.primary,
                                    borderRadius: '12px',
                                    fontWeight: '600'
                                  }}>
                                    <FaRobot style={{ fontSize: '0.65rem' }} />
                                    {language === 'ar' ? 'تلقائي' : 'Auto'}
                                  </span>
                                )}
                                <span style={{
                        fontSize: '0.75rem',
                                  padding: '3px 8px',
                                  background: reminder.urgency.color + '20',
                                  color: reminder.urgency.color,
                                  borderRadius: '6px',
                                  fontWeight: '700',
                                  textTransform: 'uppercase'
                                }}>
                                  {reminder.urgency.label}
                                </span>
                              </div>
                              <p style={{ margin: '0', fontSize: '0.85rem', color: '#6b7280' }}>
                                <FaUserFriends style={{ fontSize: '0.75rem', marginRight: '4px' }} />
                                {reminder.contact_name} • <FaClock style={{ fontSize: '0.75rem', marginLeft: '4px', marginRight: '4px' }} />
                                {new Date(reminder.reminder_date).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleCompleteReminder(reminder.id)}
                              style={{
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(22, 163, 74, 0.3)';
                              }}
                            >
                              <FaCheckCircle style={{ fontSize: '0.9rem' }} />
                              {language === 'ar' ? 'تم' : 'Done'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Today's Reminders */}
                {hasToday && (
                  <div style={{ marginBottom: hasWeek ? '24px' : '0' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '12px',
                      padding: '8px 12px',
                      background: '#fff7ed',
                      borderRadius: '8px',
                      border: '1px solid #fdba74'
                    }}>
                      <span style={{ fontSize: '1rem' }}>🟠</span>
                      <span style={{ 
                        fontWeight: '700', 
                        color: '#9a3412',
                        fontSize: '0.95rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {language === 'ar' ? 'اليوم' : 'TODAY'} ({groupedReminders.today.length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {groupedReminders.today.map((reminder) => (
                        <div key={reminder.id} style={{
                          padding: '16px',
                          background: reminder.urgency.bg,
                          borderRadius: '10px',
                          border: `2px solid ${reminder.urgency.border}`,
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#1f2937', fontSize: '0.95rem' }}>{reminder.title}</strong>
                                {reminder.is_auto && (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '0.7rem',
                        padding: '2px 8px',
                        background: colors.primary + '20',
                        color: colors.primary,
                        borderRadius: '12px',
                                    fontWeight: '600'
                                  }}>
                                    <FaRobot style={{ fontSize: '0.65rem' }} />
                        {language === 'ar' ? 'تلقائي' : 'Auto'}
                      </span>
                    )}
                                <span style={{
                                  fontSize: '0.75rem',
                                  padding: '3px 8px',
                                  background: reminder.urgency.color + '20',
                                  color: reminder.urgency.color,
                                  borderRadius: '6px',
                                  fontWeight: '700'
                                }}>
                                  {reminder.urgency.label}
                                </span>
                  </div>
                              <p style={{ margin: '0', fontSize: '0.85rem', color: '#6b7280' }}>
                                <FaUserFriends style={{ fontSize: '0.75rem', marginRight: '4px' }} />
                                {reminder.contact_name} • <FaClock style={{ fontSize: '0.75rem', marginLeft: '4px', marginRight: '4px' }} />
                                {new Date(reminder.reminder_date).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleCompleteReminder(reminder.id)}
                              style={{
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(22, 163, 74, 0.3)';
                              }}
                            >
                              <FaCheckCircle style={{ fontSize: '0.9rem' }} />
                              {language === 'ar' ? 'تم' : 'Done'}
                            </button>
                          </div>
                </div>
              ))}
            </div>
          </div>
        )}

                {/* This Week's Reminders (collapsed by default, show first 3) */}
                {hasWeek && (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px',
                      marginBottom: '12px',
                      padding: '8px 12px',
                      background: '#fffbeb',
                      borderRadius: '8px',
                      border: '1px solid #fde68a'
                    }}>
                      <span style={{ fontSize: '1rem' }}>🟡</span>
                      <span style={{ 
                        fontWeight: '700', 
                        color: '#854d0e',
                        fontSize: '0.95rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {language === 'ar' ? 'هذا الأسبوع' : 'THIS WEEK'} ({groupedReminders.week.length})
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {groupedReminders.week.slice(0, 3).map((reminder) => (
                        <div key={reminder.id} style={{
                          padding: '14px',
                          background: reminder.urgency.bg,
                          borderRadius: '10px',
                          border: `1px solid ${reminder.urgency.border}`,
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#1f2937', fontSize: '0.9rem' }}>{reminder.title}</strong>
                                {reminder.is_auto && (
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    fontSize: '0.65rem',
                                    padding: '2px 6px',
                                    background: colors.primary + '20',
                                    color: colors.primary,
                                    borderRadius: '10px',
                                    fontWeight: '600'
                                  }}>
                                    <FaRobot style={{ fontSize: '0.6rem' }} />
                                    {language === 'ar' ? 'تلقائي' : 'Auto'}
                                  </span>
                                )}
                              </div>
                              <p style={{ margin: '0', fontSize: '0.8rem', color: '#9ca3af' }}>
                                {reminder.contact_name} • {new Date(reminder.reminder_date).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleCompleteReminder(reminder.id)}
                              style={{
                                padding: '6px 12px',
                                background: '#f9fafb',
                                color: '#16a34a',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#16a34a';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.borderColor = '#16a34a';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#f9fafb';
                                e.currentTarget.style.color = '#16a34a';
                                e.currentTarget.style.borderColor = '#e5e7eb';
                              }}
                            >
                              {language === 'ar' ? 'تم' : 'Done'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {groupedReminders.week.length > 3 && (
                      <div style={{ 
                        textAlign: 'center',
                        marginTop: '12px',
                        padding: '10px',
                        color: '#6b7280',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        +{groupedReminders.week.length - 3} {language === 'ar' ? 'تذكيرات أخرى' : 'more reminders'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Interactive Deal Assignments Visualization - Owner Only */}
        {isOwner && (
          <div style={{ marginTop: '40px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#1a1a1a' }}>
                <FaChartLine style={{ color: '#4a4a4a' }} />
                {language === 'ar' ? 'الصفقات العاجلة' : 'Urgent Deals'}
              </h3>
            </div>

            {loadingDeals ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
              </div>
            ) : deals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', background: colors.neutral, borderRadius: '8px' }}>
                <FaHandshake style={{ fontSize: '3rem', color: colors.textMuted, marginBottom: '15px' }} />
                <p style={{ color: colors.textSecondary }}>{language === 'ar' ? 'لا توجد صفقات' : 'No deals yet'}</p>
              </div>
            ) : (
              <div className="urgent-deals-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
                {deals.slice(0, 6).map((deal) => {
                  const assignments = dealAssignments[deal.id] || [];
                  console.log(`Dashboard - Deal ${deal.id} assignments:`, assignments);
                  const assignedEmployees = assignments.length > 0 
                    ? assignments.map(a => a.employee_name || a.name || `Employee ${a.employee_id}`).join(', ')
                    : (language === 'ar' ? 'لا يوجد موظفين' : 'No employees');
                  
                  return (
                    <div
                      key={deal.id}
                      className="urgent-deal-card"
                      onClick={() => onNavigate && onNavigate('deals')}
                      style={{
                        padding: '20px',
                        background: '#ffffff',
                        borderRadius: '12px',
                        border: `2px solid ${colors.border}`,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        boxSizing: 'border-box',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                        e.currentTarget.style.borderColor = colors.borderHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                        e.currentTarget.style.borderColor = colors.border;
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '600' }}>
                            {deal.contact_name || (language === 'ar' ? 'عميل غير معروف' : 'Unknown Client')}
                          </h4>
                          {deal.property_address && (
                            <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: colors.textSecondary }}>
                              {deal.property_address}
                            </p>
                          )}
                          {deal.price && (
                            <p style={{ margin: '5px 0', fontSize: '1.1rem', fontWeight: 'bold', color: '#1a1a1a' }}>
                              {deal.price}
                            </p>
                          )}
                        </div>
                        <span
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            background: deal.status === 'completed' ? '#e8e8e8' : deal.status === 'in_progress' ? '#f0f0f0' : colors.medium.bg,
                            color: deal.status === 'completed' ? '#1a1a1a' : deal.status === 'in_progress' ? '#1a1a1a' : colors.medium.text,
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            border: '1px solid #d0d0d0'
                          }}
                        >
                          {deal.status === 'completed' 
                            ? (language === 'ar' ? 'مكتمل' : 'Completed')
                            : deal.status === 'in_progress'
                            ? (language === 'ar' ? 'قيد التنفيذ' : 'In Progress')
                            : (language === 'ar' ? 'قيد الانتظار' : 'Pending')}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      {deal.progress !== undefined && (
                        <div style={{ marginBottom: '15px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                              {language === 'ar' ? 'التقدم' : 'Progress'}
                            </span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: colors.primary }}>
                              {deal.progress || 0}%
                            </span>
                          </div>
                          <div style={{ width: '100%', height: '10px', background: colors.high.bg, borderRadius: '5px', overflow: 'hidden' }}>
                            <div
                              style={{
                                width: `${deal.progress || 0}%`,
                                height: '100%',
                                background: colors.primary,
                                transition: 'width 0.3s'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Priority Status and Time to Finish - Separate Display */}
                      {(() => {
                        const targetDate = deal.due_date || deal.deal_date;
                        if (!targetDate) return null;
                        
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const due = new Date(targetDate);
                        due.setHours(0, 0, 0, 0);
                        const diffTime = due - today;
                        const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        
                        // Calculate priority if not set
                        let priorityLevel = deal.priority_level;
                        if (!priorityLevel) {
                          const price = parseFloat(deal.price?.replace(/[^\d.]/g, '')) || 0;
                          const progress = deal.progress || 0;
                          let score = 0;
                          score += (price / 1000000) * 40; // Price score (40% weight)
                          
                          let urgencyScore = 50;
                          if (daysUntil < 0) urgencyScore = 100;
                          else if (daysUntil === 0) urgencyScore = 90;
                          else if (daysUntil <= 3) urgencyScore = 80;
                          else if (daysUntil <= 7) urgencyScore = 60;
                          else urgencyScore = 40;
                          score += urgencyScore * 0.4;
                          score += progress * 0.2;
                          
                          if (score >= 80) priorityLevel = 'critical';
                          else if (score >= 60) priorityLevel = 'high';
                          else if (score >= 40) priorityLevel = 'medium';
                          else priorityLevel = 'low';
                        }
                        
                        const priorityColorMap = {
                          critical: colors.critical,
                          high: colors.high,
                          medium: colors.medium,
                          low: colors.low
                        };
                        const priorityColor = priorityColorMap[priorityLevel] || priorityColorMap.medium;
                        
                        return (
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '8px', 
                            marginBottom: '15px',
                            padding: '10px',
                            background: colors.neutral,
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`
                          }}>
                            {/* Priority Status */}
                            {priorityLevel && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: colors.textSecondary }}>
                                  {language === 'ar' ? 'حالة الأولوية:' : 'Priority Status:'}
                                </span>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  background: priorityColor.bg,
                                  border: `1px solid ${priorityColor.border}`,
                                  color: priorityColor.text,
                                  fontSize: '0.8rem',
                                  fontWeight: '600'
                                }}>
                                  {priorityLevel === 'critical' && (language === 'ar' ? '🔴 حرج' : '🔴 Critical')}
                                  {priorityLevel === 'high' && (language === 'ar' ? '🟠 عالي' : '🟠 High')}
                                  {priorityLevel === 'medium' && (language === 'ar' ? '🟡 متوسط' : '🟡 Medium')}
                                  {priorityLevel === 'low' && (language === 'ar' ? '⚪ منخفض' : '⚪ Low')}
                                </span>
                              </div>
                            )}
                            
                            {/* Time to Finish */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaClock style={{ fontSize: '14px', color: daysUntil < 0 ? colors.critical.icon : daysUntil === 0 ? colors.primary : colors.textSecondary }} />
                              <span style={{ fontSize: '0.85rem', fontWeight: '500', color: colors.textSecondary }}>
                                {language === 'ar' ? 'وقت الانتهاء:' : 'Time to Finish:'}
                              </span>
                              <span style={{
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                color: daysUntil < 0 ? colors.critical.icon : daysUntil === 0 ? colors.primary : colors.textSecondary
                              }}>
                                {daysUntil < 0 
                                  ? `${language === 'ar' ? 'متأخر' : 'Overdue'} (${Math.abs(daysUntil)} ${language === 'ar' ? 'يوم' : 'days'})`
                                  : daysUntil === 0
                                  ? `${language === 'ar' ? 'اليوم' : 'Today'}`
                                  : `${daysUntil} ${language === 'ar' ? 'أيام متبقية' : 'days left'}`
                                }
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Assigned Employees */}
                      <div style={{
                        padding: '12px',
                        background: colors.primary + '10',
                        borderRadius: '8px',
                        border: `1px solid ${colors.primary}40`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <FaUserFriends style={{ color: colors.primary, fontSize: '14px' }} />
                          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: colors.primary }}>
                            {language === 'ar' ? 'الموظفون المعينون' : 'Assigned Employees'}
                          </span>
                          {assignments.length > 0 && (
                            <span style={{
                              padding: '2px 8px',
                              background: colors.primary,
                              color: 'white',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {assignments.length}
                            </span>
                          )}
                        </div>
                        <p style={{ margin: 0, fontSize: '0.9rem', color: colors.primary, lineHeight: '1.5' }}>
                          {assignedEmployees}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;

